import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { addTeamMember, adminSummary, callAdsEvents, callAdsSummary, callEvents, callSummary, cancelBoost, cancelBusinessBookingWithReason, createBoost, createBusinessOffer, createCallAdsCampaign, deleteBusinessOffer, deleteBusinessSpecialHour, downloadBusinessBookingIcs, exportCallAdsCsv, exportCallsCsv, getBusinessBookingSettings, getBusinessHoursManage, getBusinessSpecialHoursManage, getCallAdsCampaign, getCallSettings, listBoosts, listBusinessOffersManage, listBusinessUpcomingBookings, listTeam, myBusinesses, publicBusiness, refundBusinessBooking, removeTeamMember, resendBusinessBookingConfirm, rescheduleBusinessBooking, updateBusiness, updateBusinessBookingSettings, updateBusinessHours, updateBusinessOffer, updateCallAdsCampaign, updateCallSettings, updateTeamMember, updateTeamMemberRole, upsertBusinessSpecialHour } from '../../api/businessesReader'
import {
  addBusinessBookingStaff,
  businessStripeConnectStatus,
  createBusinessBookingService,
  disconnectBookingExternalCalendar,
  createBusinessStaffTimeOff,
  deleteBusinessStaffSpecialHour,
  deleteBusinessStaffTimeOff,
  disableBusinessBookingService,
  getBusinessStaffHours,
  getBusinessStaffSpecialHours,
  getBusinessStaffTimeOff,
  listBookingExternalCalendars,
  listBusinessBookingServices,
  listBusinessBookingStaff,
  removeBusinessBookingStaff,
  replaceBusinessStaffHours,
  startGoogleCalendarConnect,
  startBusinessStripeConnect,
  syncBookingExternalCalendars,
  updateBusinessBookingService,
  updateBusinessBookingStaff,
  upsertBusinessStaffSpecialHour,
} from '../../api/bookings'
import StaffScheduleEditor from '../../components/booking/StaffScheduleEditor'
import OfferImageUploader from '../../components/businesses/OfferImageUploader'
import LocationPickerModal from '../../components/businesses/LocationPickerModal'
import { getToken } from '../../lib/auth'
import { formatCoord, parseLatLng } from '../../lib/locationParse'
import { getTimeZoneIds, isValidTimeZoneId } from '../../lib/timezones'
import { useMe } from '../../hooks/useMe'
import { getAppVersionMeta, type AppVersionMeta } from '../../api/meta'
import TimezoneSelect from '../../components/TimezoneSelect'
import type { BusinessAdminSummary, BusinessBoost, BusinessBookingItem, BusinessBookingSettings, BusinessCallAdCampaign, BusinessCallEventRow, BusinessCallsSummary, BusinessHourRange, BusinessOffer, BusinessHoursGrouped, BusinessProfile, BusinessSpecialHour, BusinessTeamMember, CallSettings, SponsoredLeadEvent } from '../../types/businesses'
import type { BusinessCallAdsSummary } from '../../types/businesses'
import type { BookingService, BookingStaffMember, ExternalCalendarConnection, StripeConnectStatus } from '../../types/bookings'

type AdminTab = 'overview' | 'promotion' | 'offers' | 'ads' | 'calls' | 'team' | 'hours' | 'booking' | 'page_settings'

function clsActive(active: boolean) {
  return active
    ? 'border-brand text-white bg-white/10'
    : 'border-transparent text-white/70 hover:text-white hover:bg-white/5'
}

function isValidUrl(value: string): boolean {
  if (!value.trim()) return true
  try {
    const u = new URL(value)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

function normalizeWebsite(value: string): string {
  const raw = value.trim()
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
  return `https://${raw}`
}

function previewFromBusiness(b: BusinessProfile) {
  return {
    name: b.name || '',
    category: b.category || '',
    description: b.description || '',
    phone: b.phone || '',
    website: b.website || '',
    address: b.address || '',
    lat: b.lat == null ? '' : String(b.lat),
    lng: b.lng == null ? '' : String(b.lng),
    logo_image_url: b.logo_image_url || '',
    cover_image_url: b.cover_image_url || '',
  }
}

function relativeTimeFromNow(iso: string): string {
  const t = new Date(iso).getTime()
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (diffSec < 60) return `${diffSec}s ago`
  const m = Math.floor(diffSec / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

function callStatusUi(status: string): { label: string; className: string } {
  if (status === 'CHARGED') return { label: 'Charged', className: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' }
  if (status === 'SKIPPED_NO_PRICE') return { label: 'Duplicate/No charge', className: 'border-sky-500/30 bg-sky-500/15 text-sky-200' }
  if (status === 'SKIPPED_NO_BUDGET') return { label: 'No budget', className: 'border-amber-500/30 bg-amber-500/15 text-amber-200' }
  return { label: 'Failed', className: 'border-red-500/30 bg-red-500/15 text-red-200' }
}

function callAdsReasonUi(reason: string, slug: string): { label: string; href?: string } {
  if (reason === 'NO_CAMPAIGN') return { label: 'Create a campaign to be eligible.' }
  if (reason === 'PAUSED') return { label: 'Campaign is paused.' }
  if (reason === 'NO_PHONE') return { label: 'Add a phone number to your page.', href: `/settings/pages/${slug}/edit` }
  if (reason === 'NO_CATEGORIES') return { label: 'Select at least one category.' }
  if (reason === 'NO_RADIUS') return { label: 'Set a targeting radius.' }
  if (reason === 'OUT_OF_BUDGET') return { label: 'Daily budget exhausted for today.' }
  if (reason === 'NOT_ACTIVE') return { label: 'Setup fee is required to activate your page.' }
  return { label: reason }
}

function callBillingReasonUi(reason: string): string {
  if (reason === 'NO_PHONE') return 'Add a phone number to enable call billing.'
  if (reason === 'PENDING_SETUP_FEE') return "Page isn't active until setup fee is paid."
  if (reason === 'NOT_ACTIVE') return 'Page is not active.'
  if (reason === 'CALL_ROUTING_DISABLED') return 'Call routing is disabled.'
  return reason
}

function teamPublicStatusUi(status: BusinessTeamMember['public_status']): { label: string; className: string } {
  if (status === 'HIDDEN_BY_BUSINESS') {
    return { label: 'Hidden by you', className: 'border-amber-500/30 bg-amber-500/15 text-amber-200' }
  }
  if (status === 'HIDDEN_BY_MEMBER') {
    return { label: 'Hidden by member', className: 'border-white/20 bg-white/10 text-white/75' }
  }
  return { label: 'Public', className: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' }
}

function offerStatusUi(offer: BusinessOffer): { label: string; className: string } {
  const now = Date.now()
  const startsAt = offer.starts_at ? new Date(offer.starts_at).getTime() : null
  const endsAt = offer.ends_at ? new Date(offer.ends_at).getTime() : null
  if (!offer.is_active) return { label: 'Disabled', className: 'border-white/20 bg-white/10 text-white/75' }
  if (startsAt && startsAt > now) return { label: 'Scheduled', className: 'border-sky-500/30 bg-sky-500/15 text-sky-200' }
  if (endsAt && endsAt <= now) return { label: 'Expired', className: 'border-amber-500/30 bg-amber-500/15 text-amber-200' }
  return { label: 'Active', className: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' }
}

function bookingPaymentStatusUi(status: BusinessBookingItem['payment_status']): { label: string; className: string } {
  if (status === 'PAID') return { label: 'Paid', className: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' }
  if (status === 'REQUIRES_PAYMENT') return { label: 'Requires payment', className: 'border-amber-500/30 bg-amber-500/15 text-amber-200' }
  if (status === 'FAILED') return { label: 'Payment failed', className: 'border-red-500/30 bg-red-500/15 text-red-200' }
  if (status === 'REFUNDED') return { label: 'Refunded', className: 'border-sky-500/30 bg-sky-500/15 text-sky-200' }
  return { label: 'No payment', className: 'border-white/20 bg-white/10 text-white/75' }
}

function formatUsdCents(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '-'
  return `$${(Number(value) / 100).toFixed(2)}`
}

const CALL_ADS_CATEGORY_SUGGESTIONS = ['telecommunications', 'services', 'home services', 'health', 'food']
const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
type HoursRangeEditor = { opens_at: string; closes_at: string }
type HoursDayEditor = { open: boolean; ranges: HoursRangeEditor[] }
type HoursEditor = Record<number, HoursDayEditor>

function parseCategoriesInput(value: string): string[] {
  const items = value
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
  return Array.from(new Set(items))
}

function hhmm(value: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const [h = '00', m = '00'] = raw.split(':')
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`
}

function defaultHoursEditor(): HoursEditor {
  const days: HoursEditor = {} as HoursEditor
  for (let i = 0; i < 7; i += 1) {
    days[i] = i < 5
      ? { open: true, ranges: [{ opens_at: '09:00', closes_at: '17:00' }] }
      : { open: false, ranges: [] }
  }
  return days
}

function hoursEditorFromResponse(data: BusinessHoursGrouped): HoursEditor {
  const out: HoursEditor = {} as HoursEditor
  for (let i = 0; i < 7; i += 1) {
    const ranges = Array.isArray(data?.days?.[String(i)]) ? data.days[String(i)] : []
    out[i] = {
      open: ranges.length > 0,
      ranges: ranges.map((r) => ({
        opens_at: hhmm(r.opens_at),
        closes_at: hhmm(r.closes_at),
      })),
    }
  }
  return out
}

function hoursSnapshot(tz: string, hours: HoursEditor): string {
  const normalized = {
    timezone: String(tz || '').trim(),
    hours: Object.fromEntries(
      Array.from({ length: 7 }).map((_, idx) => [
        idx,
        {
          open: Boolean(hours[idx]?.open),
          ranges: (hours[idx]?.ranges || []).map((r) => ({ opens_at: hhmm(r.opens_at), closes_at: hhmm(r.closes_at) })),
        },
      ]),
    ),
  }
  return JSON.stringify(normalized)
}

function trackPagesEvent(eventName: 'pages_admin_open' | 'pages_admin_tab_change' | 'pages_admin_click_boost', props: Record<string, any>) {
  try {
    const anyWindow = window as any
    if (anyWindow?.analytics?.track && typeof anyWindow.analytics.track === 'function') {
      anyWindow.analytics.track(eventName, props)
      return
    }
    window.dispatchEvent(new CustomEvent('pixeloop:analytics', { detail: { event: eventName, props } }))
  } catch {}
}

function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

function fromDateTimeLocalValue(value: string): string | null {
  const raw = String(value || '').trim()
  if (!raw) return null
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

type BookingSettingsFormState = {
  timezone: string
  requires_deposit: boolean
  deposit_type: 'NONE' | 'FIXED' | 'PERCENT'
  deposit_amount_cents: string
  deposit_percent_bps: string
  currency: string
  slot_duration_minutes: string
  buffer_minutes: string
  lead_time_minutes: string
  booking_window_days: string
  max_bookings_per_day: string
  allow_customer_cancel: boolean
  allow_customer_reschedule: boolean
  cancel_cutoff_minutes: string
  reschedule_cutoff_minutes: string
  is_enabled: boolean
}

function bookingFormSnapshot(form: BookingSettingsFormState): string {
  return JSON.stringify({
    timezone: String(form.timezone || '').trim(),
    requires_deposit: Boolean(form.requires_deposit),
    deposit_type: form.deposit_type,
    deposit_amount_cents: String(form.deposit_amount_cents || '').trim(),
    deposit_percent_bps: String(form.deposit_percent_bps || '').trim(),
    currency: String(form.currency || '').trim().toLowerCase(),
    slot_duration_minutes: String(form.slot_duration_minutes || '').trim(),
    buffer_minutes: String(form.buffer_minutes || '').trim(),
    lead_time_minutes: String(form.lead_time_minutes || '').trim(),
    booking_window_days: String(form.booking_window_days || '').trim(),
    max_bookings_per_day: String(form.max_bookings_per_day || '').trim(),
    allow_customer_cancel: Boolean(form.allow_customer_cancel),
    allow_customer_reschedule: Boolean(form.allow_customer_reschedule),
    cancel_cutoff_minutes: String(form.cancel_cutoff_minutes || '').trim(),
    reschedule_cutoff_minutes: String(form.reschedule_cutoff_minutes || '').trim(),
    is_enabled: Boolean(form.is_enabled),
  })
}

function formatDateInTimezone(iso: string, timezone: string, withTime = true): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || 'UTC',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    }).format(date)
  } catch {
    return withTime ? date.toLocaleString() : date.toLocaleDateString()
  }
}

function formatTimeInTimezone(iso: string, timezone: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || 'UTC',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date)
  } catch {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }
}

function formatWhenCell(startIso: string, endIso: string, timezone: string): { dateLabel: string; timeLabel: string; title: string } {
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { dateLabel: '-', timeLabel: '-', title: '-' }
  }
  const tz = timezone || 'UTC'
  try {
    const dateFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    const timeFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
    })
    const dateLabel = dateFmt.format(start)
    const timeLabel = `${timeFmt.format(start)} - ${timeFmt.format(end)}`
    return { dateLabel, timeLabel, title: `${dateLabel} ${timeLabel} (${tz})` }
  } catch {
    const dateLabel = start.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    const timeLabel = `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    return { dateLabel, timeLabel, title: `${dateLabel} ${timeLabel}` }
  }
}

function BookingSkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="animate-pulse rounded-lg border border-white/10 bg-black/30 px-3 py-3">
          <div className="h-3 w-1/3 rounded bg-white/10" />
          <div className="mt-2 h-2.5 w-2/3 rounded bg-white/10" />
        </div>
      ))}
    </div>
  )
}

function BookingSkeletonTable({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, idx) => (
        <div key={idx} className="animate-pulse rounded-lg border border-white/10 bg-black/30 p-2">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
            <div className="h-3 rounded bg-white/10" />
            <div className="h-3 rounded bg-white/10" />
            <div className="h-3 rounded bg-white/10" />
            <div className="h-3 rounded bg-white/10" />
            <div className="h-3 rounded bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function PageAdmin() {
  const authed = Boolean(getToken())
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromQuery = (searchParams.get('tab') || '').toLowerCase()
  const initialTab: AdminTab =
    tabFromQuery === 'promotion' || tabFromQuery === 'offers' || tabFromQuery === 'ads' || tabFromQuery === 'calls' || tabFromQuery === 'team' || tabFromQuery === 'hours' || tabFromQuery === 'booking' || tabFromQuery === 'page_settings'
      ? (tabFromQuery as AdminTab)
      : 'overview'

  const [tab, setTab] = useState<AdminTab>(initialTab)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allBusinesses, setAllBusinesses] = useState<BusinessProfile[]>([])
  const [publicMerged, setPublicMerged] = useState<BusinessProfile | null>(null)

  const [budget, setBudget] = useState('5')
  const [duration, setDuration] = useState('7')
  const [scope, setScope] = useState<'LOCAL' | 'CATEGORY' | 'SEARCH'>('LOCAL')
  const [scopeCategory, setScopeCategory] = useState('')
  const [scopeRadius, setScopeRadius] = useState('10')
  const [boostSaving, setBoostSaving] = useState(false)
  const [boostError, setBoostError] = useState<string | null>(null)
  const [boostSuccess, setBoostSuccess] = useState<string | null>(null)
  const [boostsLoading, setBoostsLoading] = useState(false)
  const [boostHistory, setBoostHistory] = useState<BusinessBoost[]>([])
  const [cancelingBoostId, setCancelingBoostId] = useState<number | null>(null)
  const [callsLoading, setCallsLoading] = useState(false)
  const [callsError, setCallsError] = useState<string | null>(null)
  const [callsSummary, setCallsSummary] = useState<BusinessCallsSummary | null>(null)
  const [callsSinceDays, setCallsSinceDays] = useState<7 | 30 | 90>(30)
  const [callsEvents, setCallsEvents] = useState<BusinessCallEventRow[]>([])
  const [callsEventsPage, setCallsEventsPage] = useState(1)
  const [callsEventsHasNext, setCallsEventsHasNext] = useState(false)
  const [callsEventsLoadingMore, setCallsEventsLoadingMore] = useState(false)
  const [callsExporting, setCallsExporting] = useState(false)
  const [callSettings, setCallSettings] = useState<CallSettings | null>(null)
  const [callSettingsForm, setCallSettingsForm] = useState({
    forwarding_phone_number: '',
    call_routing_enabled: false,
    recording_enabled: false,
    transcription_enabled: false,
    consent_mode: 'IN_APP_ONLY' as 'IN_APP_ONLY' | 'PSTN_FALLBACK',
    call_base_price_override_pixe: '',
  })
  const [callSettingsSaving, setCallSettingsSaving] = useState(false)
  const [callSettingsError, setCallSettingsError] = useState<string | null>(null)
  const [callSettingsSuccess, setCallSettingsSuccess] = useState<string | null>(null)
  const [adsLoading, setAdsLoading] = useState(false)
  const [adsSaving, setAdsSaving] = useState(false)
  const [adsError, setAdsError] = useState<string | null>(null)
  const [adsSuccess, setAdsSuccess] = useState<string | null>(null)
  const [adsEventsError, setAdsEventsError] = useState<string | null>(null)
  const [adsEvents, setAdsEvents] = useState<SponsoredLeadEvent[]>([])
  const [adsEventsPage, setAdsEventsPage] = useState(1)
  const [adsEventsHasNext, setAdsEventsHasNext] = useState(false)
  const [adsEventsLoadingMore, setAdsEventsLoadingMore] = useState(false)
  const [adsExporting, setAdsExporting] = useState(false)
  const [teamLoading, setTeamLoading] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)
  const [teamMembers, setTeamMembers] = useState<BusinessTeamMember[]>([])
  const [inviteHandle, setInviteHandle] = useState('')
  const [inviteRole, setInviteRole] = useState<'OWNER' | 'MANAGER' | 'EDITOR' | 'EMPLOYEE'>('EMPLOYEE')
  const [inviteSaving, setInviteSaving] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [teamActionError, setTeamActionError] = useState<string | null>(null)
  const [teamBusyMemberId, setTeamBusyMemberId] = useState<number | null>(null)
  const { me } = useMe(authed)
  const [callAdsCampaign, setCallAdsCampaign] = useState<BusinessCallAdCampaign | null>(null)
  const [adsSummary, setAdsSummary] = useState<BusinessCallAdsSummary | null>(null)
  const [adsForm, setAdsForm] = useState({
    status: 'PAUSED' as 'ACTIVE' | 'PAUSED',
    max_cpc_pixe: '1.00',
    daily_budget_pixe: '10.00',
    categoriesText: 'services',
    radius_miles: '10',
  })
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [overviewError, setOverviewError] = useState<string | null>(null)
  const [overviewSummary, setOverviewSummary] = useState<BusinessAdminSummary | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [publicUrlCopied, setPublicUrlCopied] = useState(false)
  const [locating, setLocating] = useState(false)
  const [locationNotice, setLocationNotice] = useState<string | null>(null)
  const [locationInput, setLocationInput] = useState('')
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [showAdvancedLocation, setShowAdvancedLocation] = useState(false)
  const [hoursTimezone, setHoursTimezone] = useState('UTC')
  const [hoursDraft, setHoursDraft] = useState<HoursEditor>(defaultHoursEditor)
  const [hoursLoadedSnapshot, setHoursLoadedSnapshot] = useState('')
  const [hoursLoading, setHoursLoading] = useState(false)
  const [hoursSaving, setHoursSaving] = useState(false)
  const [hoursError, setHoursError] = useState<string | null>(null)
  const [hoursSuccess, setHoursSuccess] = useState<string | null>(null)
  const [bookingLoading, setBookingLoading] = useState(false)
  const [bookingSaving, setBookingSaving] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null)
  const [bookingCancelingId, setBookingCancelingId] = useState<string | null>(null)
  const [bookingReschedulingId, setBookingReschedulingId] = useState<string | null>(null)
  const [bookingRefundingId, setBookingRefundingId] = useState<string | null>(null)
  const [bookingResendingId, setBookingResendingId] = useState<string | null>(null)
  const [bookingRescheduleTarget, setBookingRescheduleTarget] = useState<BusinessBookingItem | null>(null)
  const [bookingRescheduleDateTime, setBookingRescheduleDateTime] = useState('')
  const [bookingStripe, setBookingStripe] = useState<StripeConnectStatus | null>(null)
  const [bookingStripeConnecting, setBookingStripeConnecting] = useState(false)
  const [bookingStripeDetailsOpen, setBookingStripeDetailsOpen] = useState(false)
  const [bookingSettings, setBookingSettings] = useState<BusinessBookingSettings | null>(null)
  const [bookingLoadedSnapshot, setBookingLoadedSnapshot] = useState('')
  const [bookingUpcoming, setBookingUpcoming] = useState<BusinessBookingItem[]>([])
  const [bookingServices, setBookingServices] = useState<BookingService[]>([])
  const [bookingExternalCalendars, setBookingExternalCalendars] = useState<ExternalCalendarConnection[]>([])
  const [bookingExternalSyncing, setBookingExternalSyncing] = useState(false)
  const [bookingExternalConnecting, setBookingExternalConnecting] = useState(false)
  const [bookingExternalDisconnectingId, setBookingExternalDisconnectingId] = useState<number | null>(null)
  const [bookingStaff, setBookingStaff] = useState<BookingStaffMember[]>([])
  const [bookingStaffSelectedId, setBookingStaffSelectedId] = useState<number | null>(null)
  const [bookingStaffLookup, setBookingStaffLookup] = useState('')
  const [bookingStaffAdding, setBookingStaffAdding] = useState(false)
  const [bookingServiceSaving, setBookingServiceSaving] = useState(false)
  const [bookingServiceError, setBookingServiceError] = useState<string | null>(null)
  const [bookingServiceForm, setBookingServiceForm] = useState({
    id: null as number | null,
    name: '',
    duration_minutes: '30',
    capacity: '1',
    price_cents: '',
    is_active: true,
  })
  const [bookingForm, setBookingForm] = useState({
    timezone: 'UTC',
    requires_deposit: false,
    deposit_type: 'NONE' as 'NONE' | 'FIXED' | 'PERCENT',
    deposit_amount_cents: '',
    deposit_percent_bps: '',
    currency: 'usd',
    slot_duration_minutes: '30',
    buffer_minutes: '0',
    lead_time_minutes: '120',
    booking_window_days: '30',
    max_bookings_per_day: '',
    allow_customer_cancel: true,
    allow_customer_reschedule: true,
    cancel_cutoff_minutes: '1440',
    reschedule_cutoff_minutes: '1440',
    is_enabled: false,
  })
  const bookingServiceNameInputRef = useRef<HTMLInputElement | null>(null)
  const bookingStaffLookupInputRef = useRef<HTMLInputElement | null>(null)
  const [specialHours, setSpecialHours] = useState<BusinessSpecialHour[]>([])
  const [specialHoursLoading, setSpecialHoursLoading] = useState(false)
  const [specialHoursError, setSpecialHoursError] = useState<string | null>(null)
  const [specialHourSaving, setSpecialHourSaving] = useState(false)
  const [specialHourDeletingId, setSpecialHourDeletingId] = useState<number | null>(null)
  const [specialDate, setSpecialDate] = useState('')
  const [specialIsClosed, setSpecialIsClosed] = useState(true)
  const [specialOpensAt, setSpecialOpensAt] = useState('09:00')
  const [specialClosesAt, setSpecialClosesAt] = useState('17:00')
  const [specialNote, setSpecialNote] = useState('')
  const [offersLoading, setOffersLoading] = useState(false)
  const [offersError, setOffersError] = useState<string | null>(null)
  const [offersSuccess, setOffersSuccess] = useState<string | null>(null)
  const [offersList, setOffersList] = useState<BusinessOffer[]>([])
  const [offerSaving, setOfferSaving] = useState(false)
  const [offerDeletingId, setOfferDeletingId] = useState<number | null>(null)
  const [offerTogglingId, setOfferTogglingId] = useState<number | null>(null)
  const [offerEditId, setOfferEditId] = useState<number | null>(null)
  const [offerModalOpen, setOfferModalOpen] = useState(false)
  const [offerForm, setOfferForm] = useState({
    title: '',
    body: '',
    cta_type: 'CALL' as 'CALL' | 'WEBSITE' | 'LINK' | 'NONE',
    cta_url: '',
    image_url: '',
    starts_at: '',
    ends_at: '',
    is_active: true,
  })
  const [appVersionMeta, setAppVersionMeta] = useState<AppVersionMeta | null>(null)

  const [preview, setPreview] = useState({
    name: '',
    category: '',
    description: '',
    phone: '',
    website: '',
    address: '',
    lat: '',
    lng: '',
    logo_image_url: '',
    cover_image_url: '',
  })

  useEffect(() => {
    if (!authed) {
      navigate('/login')
      return
    }
  }, [authed, navigate])

  useEffect(() => {
    let cancelled = false
    getAppVersionMeta()
      .then((meta) => {
        if (!cancelled) setAppVersionMeta(meta)
      })
      .catch(() => {
        if (!cancelled) setAppVersionMeta(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!authed) return
    let cancelled = false
    setLoading(true)
    setError(null)
    myBusinesses()
      .then((rows) => {
        if (cancelled) return
        setAllBusinesses(rows)
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load businesses')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [authed])

  const mineBusiness = useMemo(() => {
    return allBusinesses.find((b) => b.slug === slug) || null
  }, [allBusinesses, slug])

  const effective = publicMerged || mineBusiness

  useEffect(() => {
    if (!mineBusiness) return
    setPreview(previewFromBusiness(mineBusiness))
    setFieldErrors({})
    setScopeCategory(mineBusiness.category || '')
    setLocationNotice(null)
    setLocationInput('')
  }, [mineBusiness])

  useEffect(() => {
    if (!mineBusiness) return
    let cancelled = false
    publicBusiness(mineBusiness.slug)
      .then((pub) => {
        if (!cancelled) setPublicMerged(pub)
      })
      .catch(() => {
        if (!cancelled) setPublicMerged(null)
      })
    return () => {
      cancelled = true
    }
  }, [mineBusiness])

  useEffect(() => {
    if (!effective) return
    trackPagesEvent('pages_admin_open', { slug: effective.slug, business_id: effective.id })
  }, [effective?.id])

  useEffect(() => {
    if (!saveSuccess) return
    const t = window.setTimeout(() => setSaveSuccess(null), 2000)
    return () => window.clearTimeout(t)
  }, [saveSuccess])

  useEffect(() => {
    if (!hoursSuccess) return
    const t = window.setTimeout(() => setHoursSuccess(null), 2000)
    return () => window.clearTimeout(t)
  }, [hoursSuccess])

  useEffect(() => {
    if (!offersSuccess) return
    const t = window.setTimeout(() => setOffersSuccess(null), 2000)
    return () => window.clearTimeout(t)
  }, [offersSuccess])

  useEffect(() => {
    if (!bookingSuccess) return
    const t = window.setTimeout(() => setBookingSuccess(null), 2000)
    return () => window.clearTimeout(t)
  }, [bookingSuccess])

  useEffect(() => {
    if (!publicUrlCopied) return
    const t = window.setTimeout(() => setPublicUrlCopied(false), 2000)
    return () => window.clearTimeout(t)
  }, [publicUrlCopied])

  useEffect(() => {
    if (searchParams.get('tab') === tab) return
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }, [tab, searchParams, setSearchParams])

  useEffect(() => {
    if (tab !== 'booking') return
    const connected = (searchParams.get('calendar_connected') || '').trim().toLowerCase()
    const calError = (searchParams.get('calendar_error') || '').trim().toLowerCase()
    if (!connected && !calError) return
    if (connected === 'google') {
      setBookingSuccess('Google Calendar connected.')
    } else if (calError) {
      setBookingError('Google Calendar connection failed. Please try again.')
    }
    const next = new URLSearchParams(searchParams)
    next.delete('calendar_connected')
    next.delete('calendar_error')
    setSearchParams(next, { replace: true })
  }, [tab, searchParams, setSearchParams])

  const changeTab = (next: AdminTab) => {
    setTab(next)
    if (effective) {
      trackPagesEvent('pages_admin_tab_change', { slug: effective.slug, tab: next, business_id: effective.id })
    }
  }

  const selectedSlug = slug
  const rep = String(effective?.reputation_score ?? '0')
  const statusLabel = effective?.setup_fee_paid ? 'Active' : 'Pending'
  const statusClass = effective?.setup_fee_paid
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
    : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
  const teamCount = Array.isArray(effective?.memberships) ? effective!.memberships!.length : null
  const ageDays = effective?.created_at ? Math.max(0, Math.floor((Date.now() - new Date(effective.created_at).getTime()) / 86400000)) : 0
  const summaryStatusActive = overviewSummary?.status === 'active'
  const summaryStatusLabel = overviewSummary ? (summaryStatusActive ? 'Active' : 'Pending') : statusLabel
  const summaryRep = overviewSummary?.reputation_score ?? rep
  const summaryMembers = overviewSummary?.members_count ?? teamCount ?? 1
  const spendThisMonth = overviewSummary?.spend_this_month_pixe ?? '0.00'
  const summaryAgeDays = overviewSummary?.page_age_days ?? ageDays
  const completionFlags = overviewSummary?.completion_flags ?? {
    has_logo: Boolean((effective?.logo_image_url || '').trim()),
    has_cover: Boolean((effective?.cover_image_url || '').trim()),
    has_story: Boolean((effective?.story_body || '').trim()),
    has_phone: Boolean((effective?.phone || '').trim()),
    has_website: Boolean((effective?.website || '').trim()),
  }
  const completionRows = [
    { key: 'has_logo', label: 'Logo image', done: completionFlags.has_logo, href: `/settings/pages/${effective?.slug}/edit` },
    { key: 'has_cover', label: 'Cover image', done: completionFlags.has_cover, href: `/settings/pages/${effective?.slug}/edit` },
    { key: 'has_story', label: 'Business story', done: completionFlags.has_story, href: `/settings/pages/${effective?.slug}/edit` },
    { key: 'has_phone', label: 'Phone number', done: completionFlags.has_phone, href: `/settings/pages/${effective?.slug}?tab=page_settings` },
    { key: 'has_website', label: 'Website', done: completionFlags.has_website, href: `/settings/pages/${effective?.slug}?tab=page_settings` },
  ]
  const completionDoneCount = completionRows.filter((row) => row.done).length

  const budgetNum = Number(budget || '0')
  const budgetValid = Number.isFinite(budgetNum) && budgetNum > 0
  const durationNum = Number(duration || '0')
  const durationValid = Number.isFinite(durationNum) && durationNum >= 1 && durationNum <= 30
  const radiusNum = Number(scopeRadius || '0')
  const radiusValid = scope !== 'LOCAL' || (Number.isFinite(radiusNum) && radiusNum > 0)
  const categoryValid = scope !== 'CATEGORY' || scopeCategory.trim().length > 0
  const nowMs = Date.now()
  const isBoostActiveNow = (b: BusinessBoost) => {
    const starts = new Date(b.starts_at).getTime()
    const ends = new Date(b.ends_at).getTime()
    return Boolean(b.is_active && starts <= nowMs && ends > nowMs)
  }
  const activeBoost = boostHistory.find(isBoostActiveNow) || null
  const boostSpend30d = overviewSummary?.spend_breakdown?.boosts ?? '0.00'
  const callSpend30d = overviewSummary?.spend_breakdown?.calls ?? '0.00'
  const adsCategories = parseCategoriesInput(adsForm.categoriesText)
  const adsMaxCpcNum = Number(adsForm.max_cpc_pixe || '0')
  const adsDailyBudgetNum = Number(adsForm.daily_budget_pixe || '0')
  const adsRadiusNum = Number(adsForm.radius_miles || '0')
  const adsMaxCpcValid = Number.isFinite(adsMaxCpcNum) && adsMaxCpcNum > 0
  const adsDailyBudgetValid = Number.isFinite(adsDailyBudgetNum) && adsDailyBudgetNum >= adsMaxCpcNum
  const adsCategoriesValid = adsCategories.length > 0
  const adsRadiusValid = Number.isFinite(adsRadiusNum) && adsRadiusNum > 0
  const adsFormValid = adsMaxCpcValid && adsDailyBudgetValid && adsCategoriesValid && adsRadiusValid
  const offerNeedsUrl = offerForm.cta_type === 'LINK'
  const offerWebsiteNeedsUrl = offerForm.cta_type === 'WEBSITE'
  const offerHasRequiredUrl = !offerNeedsUrl || offerForm.cta_url.trim().length > 0
  const offerUrlFormatValid = !offerForm.cta_url.trim() || isValidUrl(offerForm.cta_url.trim())
  const offerHasTitle = offerForm.title.trim().length > 0
  const offerDateWindowValid = !offerForm.starts_at || !offerForm.ends_at || new Date(offerForm.ends_at).getTime() > new Date(offerForm.starts_at).getTime()
  const offerFormValid = offerHasTitle && offerHasRequiredUrl && offerDateWindowValid && offerUrlFormatValid
  const activeOffersCount = offersList.filter((o) => offerStatusUi(o).label === 'Active').length
  const campaignCategoriesText = callAdsCampaign?.categories?.join(', ') || ''
  const campaignRadiusStr = callAdsCampaign?.radius_miles == null ? '' : String(callAdsCampaign.radius_miles)
  const adsDirty = callAdsCampaign
    ? (
      adsForm.status !== callAdsCampaign.status
      || adsForm.max_cpc_pixe !== String(callAdsCampaign.max_cpc_pixe)
      || adsForm.daily_budget_pixe !== String(callAdsCampaign.daily_budget_pixe)
      || parseCategoriesInput(campaignCategoriesText).join(',') !== adsCategories.join(',')
      || adsForm.radius_miles !== campaignRadiusStr
    )
    : adsFormValid
  const adsStatusLabel = !adsSummary?.campaign
    ? 'Not eligible'
    : adsSummary.campaign.status === 'PAUSED'
      ? 'Paused'
      : adsSummary.is_eligible_now
        ? 'Eligible'
        : 'Not eligible'
  const adsStatusClass = adsSummary?.campaign?.status === 'PAUSED'
    ? 'border-white/20 bg-white/10 text-white/80'
    : adsSummary?.is_eligible_now
      ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200'
      : 'border-amber-500/30 bg-amber-500/15 text-amber-200'
  const callSettingsDirty = Boolean(
    callSettings &&
    (
      callSettingsForm.forwarding_phone_number !== (callSettings.forwarding_phone_number || '') ||
      callSettingsForm.call_routing_enabled !== Boolean(callSettings.call_routing_enabled) ||
      callSettingsForm.recording_enabled !== Boolean(callSettings.recording_enabled) ||
      callSettingsForm.transcription_enabled !== Boolean(callSettings.transcription_enabled) ||
      callSettingsForm.consent_mode !== callSettings.consent_mode ||
      callSettingsForm.call_base_price_override_pixe !== (callSettings.call_base_price_override_pixe || '')
    )
  )
  const hasTrackingDid = Boolean((callSettings?.tracking_did_number || '').trim())
  const callRoutingEnabled = Boolean(callSettingsForm.call_routing_enabled)
  const billingActive = callSettings?.billing_status === 'ACTIVE'
  const billingStatusClass = billingActive
    ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200'
    : 'border-amber-500/30 bg-amber-500/15 text-amber-200'
  const overridePriceValid = callSettingsForm.call_base_price_override_pixe.trim() === '' || Number(callSettingsForm.call_base_price_override_pixe) > 0
  const callSettingsFormValid = overridePriceValid
  const viewerRole = useMemo(() => {
    if (!me?.id) return null
    return teamMembers.find((row) => row.user.id === me.id)?.role || null
  }, [me?.id, teamMembers])
  const canManageTeam = viewerRole === 'OWNER' || viewerRole === 'MANAGER'
  const canAssignOwner = viewerRole === 'OWNER'
  const canChangeRole = (member: BusinessTeamMember) => {
    if (!canManageTeam) return false
    if (viewerRole === 'MANAGER' && member.role === 'OWNER') return false
    return true
  }
  const canRemoveMember = (member: BusinessTeamMember) => {
    if (!canManageTeam) return false
    if (viewerRole === 'MANAGER' && member.role === 'OWNER') return false
    return true
  }
  const previewWebsiteValid = isValidUrl(preview.website)
  const settingsNameValid = preview.name.trim().length > 0
  const settingsCategoryValid = preview.category.trim().length > 0
  const latRaw = preview.lat.trim()
  const lngRaw = preview.lng.trim()
  const latProvided = latRaw.length > 0
  const lngProvided = lngRaw.length > 0
  const locationSet = latProvided && lngProvided && Number.isFinite(Number(latRaw)) && Number.isFinite(Number(lngRaw))
  const parsedLat = locationSet ? Number(latRaw) : null
  const parsedLng = locationSet ? Number(lngRaw) : null
  const latLngPairValid = latProvided === lngProvided
  const latValid = !latProvided || Number.isFinite(Number(latRaw))
  const lngValid = !lngProvided || Number.isFinite(Number(lngRaw))
  const settingsValid = settingsNameValid && settingsCategoryValid && previewWebsiteValid && latLngPairValid && latValid && lngValid
  const settingsDirty = Boolean(
    mineBusiness &&
    (
      preview.name !== (mineBusiness.name || '') ||
      preview.category !== (mineBusiness.category || '') ||
      preview.description !== (mineBusiness.description || '') ||
      preview.phone !== (mineBusiness.phone || '') ||
      preview.website !== (mineBusiness.website || '') ||
      preview.address !== (mineBusiness.address || '') ||
      preview.lat !== (mineBusiness.lat == null ? '' : String(mineBusiness.lat)) ||
      preview.lng !== (mineBusiness.lng == null ? '' : String(mineBusiness.lng)) ||
      preview.logo_image_url !== (mineBusiness.logo_image_url || '') ||
      preview.cover_image_url !== (mineBusiness.cover_image_url || '')
    )
  )
  const publicUrl = effective?.slug
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/b/${effective.slug}`
    : ''
  const completenessChecks = [
    Boolean(preview.logo_image_url.trim()),
    Boolean(preview.cover_image_url.trim()),
    Boolean((effective?.story_body || '').trim() || (effective?.story_title || '').trim()),
    Boolean(preview.phone.trim()),
    Boolean(normalizeWebsite(preview.website).trim()),
    Boolean(latProvided && lngProvided),
    Boolean(preview.description.trim()),
  ]
  const completenessScore = Math.round((completenessChecks.filter(Boolean).length / completenessChecks.length) * 100)
  const resolvedTimezones = useMemo(() => getTimeZoneIds(), [])
  const bookingDirty = useMemo(
    () => bookingLoadedSnapshot.length > 0 && bookingFormSnapshot(bookingForm) !== bookingLoadedSnapshot,
    [bookingLoadedSnapshot, bookingForm],
  )
  const bookingDisplayTimezone = String(bookingForm.timezone || bookingSettings?.timezone || 'UTC').trim() || 'UTC'
  const hoursDirty = hoursLoadedSnapshot.length > 0 && hoursSnapshot(hoursTimezone, hoursDraft) !== hoursLoadedSnapshot
  const hoursValidationError = useMemo(() => {
    if (!String(hoursTimezone || '').trim()) return 'Timezone is required.'
    for (let d = 0; d < 7; d += 1) {
      const day = hoursDraft[d]
      if (!day?.open) continue
      if (!day.ranges.length) return `${WEEKDAY_LABELS[d]} needs at least one time range.`
      for (const range of day.ranges) {
        const open = hhmm(range.opens_at)
        const close = hhmm(range.closes_at)
        if (!open || !close) return `${WEEKDAY_LABELS[d]} has an incomplete time range.`
        if (open >= close) return `${WEEKDAY_LABELS[d]} has a range where close time must be after open time.`
      }
    }
    return null
  }, [hoursTimezone, hoursDraft])
  const hoursValid = !hoursValidationError

  const loadBoostHistory = async (businessSlug: string) => {
    setBoostsLoading(true)
    setBoostError(null)
    try {
      const rows = await listBoosts(businessSlug)
      setBoostHistory(rows)
    } catch (e: any) {
      setBoostError(e?.response?.data?.detail || e?.message || 'Failed to load boost history.')
    } finally {
      setBoostsLoading(false)
    }
  }

  const loadTeamMembers = async (businessSlug: string) => {
    setTeamLoading(true)
    setTeamError(null)
    try {
      const rows = await listTeam(businessSlug)
      setTeamMembers(rows)
    } catch (e: any) {
      setTeamError(e?.response?.data?.detail || e?.message || 'Failed to load team members.')
    } finally {
      setTeamLoading(false)
    }
  }

  useEffect(() => {
    if (!effective?.slug || tab !== 'promotion') return
    void loadBoostHistory(effective.slug)
  }, [effective?.slug, tab])

  useEffect(() => {
    if (!effective?.slug || tab !== 'team') return
    void loadTeamMembers(effective.slug)
  }, [effective?.slug, tab])

  useEffect(() => {
    if (!effective?.slug || tab !== 'offers') return
    let cancelled = false
    setOffersLoading(true)
    setOffersError(null)
    listBusinessOffersManage(effective.slug)
      .then((rows) => {
        if (!cancelled) setOffersList(rows)
      })
      .catch((e: any) => {
        if (cancelled) return
        setOffersList([])
        setOffersError(e?.response?.data?.detail || e?.message || 'Failed to load offers.')
      })
      .finally(() => {
        if (!cancelled) setOffersLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, tab])

  useEffect(() => {
    if (!effective?.slug || tab !== 'booking') return
    let cancelled = false
    setBookingLoading(true)
    setBookingError(null)
    Promise.all([
      getBusinessBookingSettings(effective.slug),
      listBusinessUpcomingBookings(effective.slug, 30),
      businessStripeConnectStatus(effective.slug),
      listBusinessBookingServices(effective.slug),
      listBusinessBookingStaff(effective.slug),
      listBookingExternalCalendars({ provider_type: 'BUSINESS', provider_public_id: effective.slug }),
    ])
      .then(([settings, upcoming, stripeStatus, services, staff, externalCalendars]) => {
        if (cancelled) return
        setBookingSettings(settings)
        setBookingUpcoming(upcoming)
        setBookingStripe(stripeStatus)
        setBookingStripeDetailsOpen(false)
        setBookingServices(services)
        setBookingExternalCalendars(externalCalendars)
        setBookingStaff(staff)
        setBookingStaffSelectedId((prev) => {
          if (staff.length === 0) return null
          if (prev && staff.some((row) => row.id === prev)) return prev
          return staff[0].id
        })
        const nextForm: BookingSettingsFormState = {
          timezone: settings.timezone || 'UTC',
          requires_deposit: Boolean(settings.requires_deposit),
          deposit_type: (settings.deposit_type || 'NONE') as 'NONE' | 'FIXED' | 'PERCENT',
          deposit_amount_cents: settings.deposit_amount_cents == null ? '' : String(settings.deposit_amount_cents),
          deposit_percent_bps: settings.deposit_percent_bps == null ? '' : String(settings.deposit_percent_bps),
          currency: (settings.currency || 'usd').toLowerCase(),
          slot_duration_minutes: String(settings.slot_duration_minutes ?? 30),
          buffer_minutes: String(settings.buffer_minutes ?? 0),
          lead_time_minutes: String(settings.lead_time_minutes ?? 120),
          booking_window_days: String(settings.booking_window_days ?? 30),
          max_bookings_per_day: settings.max_bookings_per_day == null ? '' : String(settings.max_bookings_per_day),
          allow_customer_cancel: settings.allow_customer_cancel !== false,
          allow_customer_reschedule: settings.allow_customer_reschedule !== false,
          cancel_cutoff_minutes: String(settings.cancel_cutoff_minutes ?? 1440),
          reschedule_cutoff_minutes: String(settings.reschedule_cutoff_minutes ?? 1440),
          is_enabled: Boolean(settings.is_enabled),
        }
        setBookingForm(nextForm)
        setBookingLoadedSnapshot(bookingFormSnapshot(nextForm))
      })
      .catch((e: any) => {
        if (cancelled) return
        setBookingSettings(null)
        setBookingStripe(null)
        setBookingStripeDetailsOpen(false)
        setBookingUpcoming([])
        setBookingServices([])
        setBookingExternalCalendars([])
        setBookingStaff([])
        setBookingStaffSelectedId(null)
        setBookingLoadedSnapshot('')
        setBookingError(e?.response?.data?.detail || e?.message || 'Failed to load booking settings.')
      })
      .finally(() => {
        if (!cancelled) setBookingLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, tab])


  useEffect(() => {
    if (!effective?.slug || (tab !== 'page_settings' && tab !== 'hours')) return
    let cancelled = false
    setHoursLoading(true)
    setHoursError(null)
    getBusinessHoursManage(effective.slug)
      .then((payload) => {
        if (cancelled) return
        const tz = String(payload?.timezone || effective.timezone || 'UTC')
        const editor = hoursEditorFromResponse(payload)
        setHoursTimezone(tz)
        setHoursDraft(editor)
        setHoursLoadedSnapshot(hoursSnapshot(tz, editor))
      })
      .catch((e: any) => {
        if (cancelled) return
        const fallbackTz = String(effective.timezone || 'UTC')
        const fallback = defaultHoursEditor()
        setHoursTimezone(fallbackTz)
        setHoursDraft(fallback)
        setHoursLoadedSnapshot(hoursSnapshot(fallbackTz, fallback))
        setHoursError(e?.response?.data?.detail || e?.message || 'Failed to load hours.')
      })
      .finally(() => {
        if (!cancelled) setHoursLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, effective?.timezone, tab])

  useEffect(() => {
    if (!effective?.slug || tab !== 'hours') return
    let cancelled = false
    setSpecialHoursLoading(true)
    setSpecialHoursError(null)
    getBusinessSpecialHoursManage(effective.slug)
      .then((rows) => {
        if (cancelled) return
        setSpecialHours(rows)
      })
      .catch((e: any) => {
        if (cancelled) return
        setSpecialHours([])
        setSpecialHoursError(e?.response?.data?.detail || e?.message || 'Failed to load special hours.')
      })
      .finally(() => {
        if (!cancelled) setSpecialHoursLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, tab])

  useEffect(() => {
    if (!effective?.slug || tab !== 'ads') return
    let cancelled = false
    setAdsLoading(true)
    setAdsError(null)
    setAdsEventsError(null)
    getCallAdsCampaign(effective.slug)
      .then(async (campaign) => {
        if (cancelled) return
        setCallAdsCampaign(campaign)
        try {
          const [summary, eventsRow] = await Promise.all([
            callAdsSummary(effective.slug, 30),
            callAdsEvents(effective.slug, 30, 1, 25),
          ])
          if (!cancelled) {
            setAdsSummary(summary)
            setAdsEvents(eventsRow.results || [])
            setAdsEventsPage(eventsRow.page || 1)
            setAdsEventsHasNext(Boolean(eventsRow.has_next))
          }
        } catch {
          if (!cancelled) {
            setAdsSummary(null)
            setAdsEvents([])
            setAdsEventsPage(1)
            setAdsEventsHasNext(false)
          }
        }
        if (campaign) {
          setAdsForm({
            status: campaign.status,
            max_cpc_pixe: String(campaign.max_cpc_pixe),
            daily_budget_pixe: String(campaign.daily_budget_pixe),
            categoriesText: (campaign.categories || []).join(', '),
            radius_miles: campaign.radius_miles == null ? '' : String(campaign.radius_miles),
          })
        } else {
          setAdsForm({
            status: 'PAUSED',
            max_cpc_pixe: '1.00',
            daily_budget_pixe: '10.00',
            categoriesText: 'services',
            radius_miles: '10',
          })
        }
      })
      .catch((e: any) => {
        if (cancelled) return
        setAdsError(e?.response?.data?.detail || e?.message || 'Failed to load Call Ads campaign.')
      })
      .finally(() => {
        if (!cancelled) setAdsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, tab])

  useEffect(() => {
    if (!effective?.slug || tab !== 'calls') return
    let cancelled = false
    setCallsLoading(true)
    setCallsError(null)
    setCallSettingsError(null)
    setCallSettingsSuccess(null)
    Promise.all([
      callSummary(effective.slug, callsSinceDays),
      callEvents(effective.slug, callsSinceDays, 1, 25),
      getCallSettings(effective.slug),
    ])
      .then(([summaryRow, eventsRow, settingsRow]) => {
        if (cancelled) return
        setCallsSummary(summaryRow)
        setCallsEvents(eventsRow.results || [])
        setCallsEventsPage(eventsRow.page || 1)
        setCallsEventsHasNext(Boolean(eventsRow.has_next))
        setCallSettings(settingsRow)
        setCallSettingsForm({
          forwarding_phone_number: settingsRow.forwarding_phone_number || '',
          call_routing_enabled: Boolean(settingsRow.call_routing_enabled),
          recording_enabled: Boolean(settingsRow.recording_enabled),
          transcription_enabled: Boolean(settingsRow.transcription_enabled),
          consent_mode: settingsRow.consent_mode,
          call_base_price_override_pixe: settingsRow.call_base_price_override_pixe || '',
        })
      })
      .catch((e: any) => {
        if (cancelled) return
        setCallsError(e?.response?.data?.detail || e?.message || 'Failed to load calls data.')
      })
      .finally(() => {
        if (!cancelled) setCallsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, tab, callsSinceDays])

  useEffect(() => {
    if (!effective?.slug || (tab !== 'overview' && tab !== 'promotion')) return
    let cancelled = false
    setOverviewLoading(true)
    setOverviewError(null)
    adminSummary(effective.slug, 30)
      .then((row) => {
        if (!cancelled) setOverviewSummary(row)
      })
      .catch((e: any) => {
        if (cancelled) return
        setOverviewError(e?.response?.data?.detail || e?.message || 'Failed to load overview summary.')
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [effective?.slug, tab])

  const onCreateBoost = async () => {
    if (!effective || !budgetValid || !durationValid || !radiusValid || !categoryValid || boostSaving) return
    setBoostSaving(true)
    setBoostError(null)
    setBoostSuccess(null)
    try {
      trackPagesEvent('pages_admin_click_boost', { slug: effective.slug, budget, duration, scope })
      await createBoost(effective.slug, {
        scope,
        duration_days: durationNum,
        budget_pixe: budget,
        category: scope === 'CATEGORY' ? scopeCategory.trim() : undefined,
        radius_miles: scope === 'LOCAL' ? scopeRadius : undefined,
      })
      setBoostSuccess('Boost started successfully.')
      await loadBoostHistory(effective.slug)
      const refreshed = await adminSummary(effective.slug, 30)
      setOverviewSummary(refreshed)
    } catch (e: any) {
      setBoostError(e?.response?.data?.detail || e?.response?.data?.code || e?.message || 'Failed to start boost.')
    } finally {
      setBoostSaving(false)
    }
  }

  const onCancelBoost = async (boostId: number) => {
    if (!effective || cancelingBoostId === boostId) return
    const ok = window.confirm('Stop this boost now? No refunds in v1.')
    if (!ok) return

    setCancelingBoostId(boostId)
    setBoostError(null)
    setBoostSuccess(null)
    try {
      await cancelBoost(effective.slug, boostId)
      setBoostSuccess('Boost stopped.')
      await loadBoostHistory(effective.slug)
      const refreshed = await adminSummary(effective.slug, 30)
      setOverviewSummary(refreshed)
    } catch (e: any) {
      setBoostError(e?.response?.data?.detail || e?.message || 'Failed to stop boost.')
    } finally {
      setCancelingBoostId(null)
    }
  }

  const onLoadMoreCalls = async () => {
    if (!effective || callsEventsLoadingMore || !callsEventsHasNext) return
    const nextPage = callsEventsPage + 1
    setCallsEventsLoadingMore(true)
    setCallsError(null)
    try {
      const row = await callEvents(effective.slug, callsSinceDays, nextPage, 25)
      setCallsEvents((prev) => [...prev, ...(row.results || [])])
      setCallsEventsPage(row.page || nextPage)
      setCallsEventsHasNext(Boolean(row.has_next))
    } catch (e: any) {
      setCallsError(e?.response?.data?.detail || e?.message || 'Failed to load more call events.')
    } finally {
      setCallsEventsLoadingMore(false)
    }
  }

  const onExportCallsCsv = async () => {
    if (!effective || callsExporting) return
    setCallsExporting(true)
    setCallsError(null)
    try {
      const blob = await exportCallsCsv(effective.slug, callsSinceDays)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `business_calls_${effective.slug}_${callsSinceDays}d.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      setCallsError(e?.response?.data?.detail || e?.message || 'Failed to export CSV.')
    } finally {
      setCallsExporting(false)
    }
  }

  const onSaveCallSettings = async () => {
    if (!effective || !callSettings || !callSettingsDirty || callSettingsSaving || !callSettingsFormValid) return
    setCallSettingsSaving(true)
    setCallSettingsError(null)
    setCallSettingsSuccess(null)
    try {
      const saved = await updateCallSettings(effective.slug, {
        forwarding_phone_number: callSettingsForm.forwarding_phone_number.trim(),
        call_routing_enabled: callSettingsForm.call_routing_enabled,
        recording_enabled: callSettingsForm.recording_enabled,
        transcription_enabled: callSettingsForm.transcription_enabled,
        consent_mode: callSettingsForm.consent_mode,
        call_base_price_override_pixe: callSettingsForm.call_base_price_override_pixe.trim() ? callSettingsForm.call_base_price_override_pixe.trim() : null,
      })
      setCallSettings(saved)
      setCallSettingsForm({
        forwarding_phone_number: saved.forwarding_phone_number || '',
        call_routing_enabled: Boolean(saved.call_routing_enabled),
        recording_enabled: Boolean(saved.recording_enabled),
        transcription_enabled: Boolean(saved.transcription_enabled),
        consent_mode: saved.consent_mode,
        call_base_price_override_pixe: saved.call_base_price_override_pixe || '',
      })
      setCallSettingsSuccess('Call settings saved.')
    } catch (e: any) {
      const data = e?.response?.data
      const detail = data?.detail || (typeof data === 'string' ? data : null)
      const firstFieldError = !detail && data && typeof data === 'object'
        ? Object.values(data).find((v) => typeof v === 'string') || Object.values(data).find((v) => Array.isArray(v) && v.length)?.[0]
        : null
      setCallSettingsError((detail || firstFieldError || e?.message || 'Failed to save call settings.') as string)
    } finally {
      setCallSettingsSaving(false)
    }
  }

  const onSaveCallAdsCampaign = async () => {
    if (!effective || !adsFormValid || !adsDirty || adsSaving) return
    setAdsSaving(true)
    setAdsError(null)
    setAdsSuccess(null)
    try {
      const payload = {
        status: adsForm.status,
        max_cpc_pixe: adsForm.max_cpc_pixe,
        daily_budget_pixe: adsForm.daily_budget_pixe,
        categories: adsCategories,
        radius_miles: adsForm.radius_miles,
      }
      const saved = callAdsCampaign
        ? await updateCallAdsCampaign(effective.slug, payload)
        : await createCallAdsCampaign(effective.slug, payload)
      setCallAdsCampaign(saved)
      try {
        const summary = await callAdsSummary(effective.slug, 30)
        setAdsSummary(summary)
      } catch {}
      setAdsForm({
        status: saved.status,
        max_cpc_pixe: String(saved.max_cpc_pixe),
        daily_budget_pixe: String(saved.daily_budget_pixe),
        categoriesText: (saved.categories || []).join(', '),
        radius_miles: saved.radius_miles == null ? '' : String(saved.radius_miles),
      })
      setAdsSuccess(callAdsCampaign ? 'Campaign updated.' : 'Campaign created.')
    } catch (e: any) {
      setAdsError(e?.response?.data?.detail || e?.message || 'Failed to save Call Ads campaign.')
    } finally {
      setAdsSaving(false)
    }
  }

  const onAddTeamMember = async () => {
    if (!effective || !canManageTeam || inviteSaving) return
    const handle = inviteHandle.trim()
    if (!handle) return
    setInviteSaving(true)
    setInviteError(null)
    setTeamActionError(null)
    try {
      await addTeamMember(effective.slug, {
        handle_or_username: handle,
        role: inviteRole,
      })
      setInviteHandle('')
      setInviteRole('EMPLOYEE')
      await loadTeamMembers(effective.slug)
    } catch (e: any) {
      const data = e?.response?.data
      const msg = data?.handle_or_username || data?.detail || e?.message || 'Failed to add member.'
      setInviteError(Array.isArray(msg) ? msg[0] : msg)
    } finally {
      setInviteSaving(false)
    }
  }

  const onChangeTeamRole = async (member: BusinessTeamMember, nextRole: BusinessTeamMember['role']) => {
    if (!effective || !canChangeRole(member) || teamBusyMemberId != null) return
    if (member.role === nextRole) return
    setTeamBusyMemberId(member.id)
    setTeamActionError(null)
    try {
      await updateTeamMemberRole(effective.slug, member.id, nextRole)
      await loadTeamMembers(effective.slug)
    } catch (e: any) {
      setTeamActionError(e?.response?.data?.detail || e?.message || 'Failed to update member role.')
    } finally {
      setTeamBusyMemberId(null)
    }
  }

  const onRemoveTeamMember = async (member: BusinessTeamMember) => {
    if (!effective || !canRemoveMember(member) || teamBusyMemberId != null) return
    const label = member.user.handle || member.user.username
    const ok = window.confirm(`Remove ${label} from this page?`)
    if (!ok) return
    setTeamBusyMemberId(member.id)
    setTeamActionError(null)
    try {
      await removeTeamMember(effective.slug, member.id)
      await loadTeamMembers(effective.slug)
    } catch (e: any) {
      setTeamActionError(e?.response?.data?.detail || e?.message || 'Failed to remove member.')
    } finally {
      setTeamBusyMemberId(null)
    }
  }

  const onToggleBusinessVisibility = async (member: BusinessTeamMember) => {
    if (!effective || !canManageTeam || teamBusyMemberId != null) return
    setTeamBusyMemberId(member.id)
    setTeamActionError(null)
    try {
      await updateTeamMember(effective.slug, member.id, { is_hidden_by_business: !member.is_hidden_by_business })
      await loadTeamMembers(effective.slug)
    } catch (e: any) {
      setTeamActionError(e?.response?.data?.detail || e?.message || 'Failed to update visibility.')
    } finally {
      setTeamBusyMemberId(null)
    }
  }

  const onToggleCallAdsStatus = async (nextStatus: 'ACTIVE' | 'PAUSED') => {
    if (!effective || !callAdsCampaign || adsSaving || callAdsCampaign.status === nextStatus) return
    setAdsSaving(true)
    setAdsError(null)
    setAdsSuccess(null)
    const previous = callAdsCampaign
    setCallAdsCampaign({ ...callAdsCampaign, status: nextStatus })
    setAdsForm((p) => ({ ...p, status: nextStatus }))
    try {
      const saved = await updateCallAdsCampaign(effective.slug, { status: nextStatus })
      setCallAdsCampaign(saved)
      const summary = await callAdsSummary(effective.slug, 30)
      setAdsSummary(summary)
      setAdsSuccess(nextStatus === 'ACTIVE' ? 'Campaign resumed.' : 'Campaign paused.')
    } catch (e: any) {
      setCallAdsCampaign(previous)
      setAdsForm((p) => ({ ...p, status: previous.status }))
      setAdsError(e?.response?.data?.detail || e?.message || 'Failed to update campaign status.')
    } finally {
      setAdsSaving(false)
    }
  }

  const onLoadMoreAdsEvents = async () => {
    if (!effective || adsEventsLoadingMore || !adsEventsHasNext) return
    const nextPage = adsEventsPage + 1
    setAdsEventsLoadingMore(true)
    setAdsEventsError(null)
    try {
      const row = await callAdsEvents(effective.slug, 30, nextPage, 25)
      setAdsEvents((prev) => [...prev, ...(row.results || [])])
      setAdsEventsPage(row.page || nextPage)
      setAdsEventsHasNext(Boolean(row.has_next))
    } catch (e: any) {
      setAdsEventsError(e?.response?.data?.detail || e?.message || 'Failed to load more sponsored leads.')
    } finally {
      setAdsEventsLoadingMore(false)
    }
  }

  const onExportCallAdsCsv = async () => {
    if (!effective || adsExporting) return
    setAdsExporting(true)
    setAdsEventsError(null)
    try {
      const blob = await exportCallAdsCsv(effective.slug, 30)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `business_call_ads_${effective.slug}_30d.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      setAdsEventsError(e?.response?.data?.detail || e?.message || 'Failed to export sponsored leads CSV.')
    } finally {
      setAdsExporting(false)
    }
  }

  const setPreviewField = (key: keyof typeof preview, value: string) => {
    setPreview((p) => ({ ...p, [key]: value }))
    setSaveError(null)
    setSaveSuccess(null)
    setFieldErrors((prev) => {
      if (!prev[key] && !(key === 'lat' || key === 'lng') && !prev.non_field_errors) return prev
      const next = { ...prev }
      delete next[key]
      if (key === 'lat' || key === 'lng') {
        delete next.lat
        delete next.lng
      }
      delete next.non_field_errors
      return next
    })
  }

  const onResetPageSettings = () => {
    if (!mineBusiness) return
    setPreview(previewFromBusiness(mineBusiness))
    setFieldErrors({})
    setSaveError(null)
    setSaveSuccess(null)
    setLocationNotice(null)
    setLocationInput('')
  }

  const onCopyPublicUrl = async () => {
    if (!publicUrl) return
    try {
      await navigator.clipboard.writeText(publicUrl)
      setPublicUrlCopied(true)
    } catch {
      setSaveError('Failed to copy URL.')
    }
  }

  const setLocationValues = (lat: number, lng: number) => {
    setPreviewField('lat', String(lat))
    setPreviewField('lng', String(lng))
  }

  const onUseCurrentLocation = () => {
    if (!navigator?.geolocation) {
      setLocationNotice('Unable to determine location on this device.')
      return
    }
    setLocating(true)
    setLocationNotice(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        setLocationValues(pos.coords.latitude, pos.coords.longitude)
        setLocationNotice('Location updated from your device.')
      },
      (err) => {
        setLocating(false)
        if (err.code === 1) setLocationNotice('Location permission denied.')
        else setLocationNotice('Unable to determine location.')
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    )
  }

  const onApplyPastedLocation = () => {
    const parsed = parseLatLng(locationInput)
    if (!parsed) {
      setLocationNotice('Could not parse location. Paste coordinates or a maps link.')
      return
    }
    setLocationValues(parsed.lat, parsed.lng)
    setLocationNotice(`Location parsed: ${formatCoord(parsed.lat)}, ${formatCoord(parsed.lng)}.`)
  }

  const onClearLocation = () => {
    setPreviewField('lat', '')
    setPreviewField('lng', '')
    setLocationNotice('Location cleared.')
  }

  const onSavePageSettings = async () => {
    if (!mineBusiness || !settingsDirty || !settingsValid || saveLoading) return
    setSaveLoading(true)
    setSaveError(null)
    setSaveSuccess(null)
    setFieldErrors({})
    try {
      const payload = {
        name: preview.name.trim(),
        category: preview.category.trim(),
        description: preview.description.trim(),
        phone: preview.phone.trim(),
        website: normalizeWebsite(preview.website),
        address: preview.address.trim(),
        lat: latProvided ? Number(latRaw) : null,
        lng: lngProvided ? Number(lngRaw) : null,
        logo_image_url: preview.logo_image_url.trim(),
        cover_image_url: preview.cover_image_url.trim(),
      }
      const updated = await updateBusiness(mineBusiness.slug, payload)
      setAllBusinesses((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))
      setPublicMerged(updated)
      setPreview(previewFromBusiness(updated))
      setFieldErrors({})
      setSaveSuccess('Saved')
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      if (typeof detail === 'string' && detail) {
        setSaveError(detail)
      } else {
        const data = e?.response?.data
        if (data && typeof data === 'object') {
          const mapped: Record<string, string> = {}
          for (const [k, v] of Object.entries(data)) {
            if (Array.isArray(v) && v.length) mapped[k] = String(v[0])
            else if (typeof v === 'string') mapped[k] = v
          }
          if (Object.keys(mapped).length) setFieldErrors(mapped)
          const firstKey = Object.keys(data)[0]
          const firstValue = firstKey ? (data as any)[firstKey] : null
          if (Array.isArray(firstValue) && firstValue.length) setSaveError(String(firstValue[0]))
          else if (typeof firstValue === 'string') setSaveError(firstValue)
          else setSaveError(e?.message || 'Failed to save changes.')
        } else {
          setSaveError(e?.message || 'Failed to save changes.')
        }
      }
    } finally {
      setSaveLoading(false)
    }
  }

  const setHoursDayOpen = (weekday: number, open: boolean) => {
    setHoursDraft((prev) => {
      const next = { ...prev }
      const current = next[weekday] || { open: false, ranges: [] }
      next[weekday] = {
        open,
        ranges: open
          ? (current.ranges.length ? current.ranges : [{ opens_at: '09:00', closes_at: '17:00' }])
          : [],
      }
      return next
    })
    setHoursError(null)
    setHoursSuccess(null)
  }

  const setHoursRangeField = (weekday: number, index: number, key: keyof HoursRangeEditor, value: string) => {
    setHoursDraft((prev) => {
      const next = { ...prev }
      const day = { ...(next[weekday] || { open: true, ranges: [] }) }
      const ranges = [...(day.ranges || [])]
      const row = { ...(ranges[index] || { opens_at: '09:00', closes_at: '17:00' }) }
      row[key] = hhmm(value)
      ranges[index] = row
      day.ranges = ranges
      next[weekday] = day
      return next
    })
    setHoursError(null)
    setHoursSuccess(null)
  }

  const addHoursRange = (weekday: number) => {
    setHoursDraft((prev) => {
      const next = { ...prev }
      const day = { ...(next[weekday] || { open: true, ranges: [] }) }
      day.open = true
      day.ranges = [...(day.ranges || []), { opens_at: '09:00', closes_at: '17:00' }]
      next[weekday] = day
      return next
    })
    setHoursError(null)
    setHoursSuccess(null)
  }

  const removeHoursRange = (weekday: number, index: number) => {
    setHoursDraft((prev) => {
      const next = { ...prev }
      const day = { ...(next[weekday] || { open: true, ranges: [] }) }
      day.ranges = (day.ranges || []).filter((_, i) => i !== index)
      if (!day.ranges.length) day.open = false
      next[weekday] = day
      return next
    })
    setHoursError(null)
    setHoursSuccess(null)
  }

  const onCopyWeekdayTemplate = () => {
    setHoursDraft((prev) => {
      const template = [...(prev[0]?.ranges || [{ opens_at: '09:00', closes_at: '17:00' }])]
      const next = { ...prev }
      for (let i = 0; i < 5; i += 1) {
        next[i] = { open: true, ranges: template.map((r) => ({ ...r })) }
      }
      return next
    })
    setHoursError(null)
    setHoursSuccess(null)
  }

  const onClosedAllWeek = () => {
    setHoursDraft(() => {
      const next: HoursEditor = {} as HoursEditor
      for (let i = 0; i < 7; i += 1) next[i] = { open: false, ranges: [] }
      return next
    })
    setHoursError(null)
    setHoursSuccess(null)
  }

  const onApplyDefaultWeek = () => {
    setHoursDraft(defaultHoursEditor())
    setHoursError(null)
    setHoursSuccess(null)
  }

  const onSaveHours = async () => {
    if (!effective || !hoursDirty || !hoursValid || hoursSaving) return
    setHoursSaving(true)
    setHoursError(null)
    setHoursSuccess(null)
    try {
      const hours = Array.from({ length: 7 }).map((_, weekday) => {
        const day = hoursDraft[weekday] || { open: false, ranges: [] }
        const ranges: BusinessHourRange[] = day.open
          ? day.ranges
            .map((r) => ({ opens_at: hhmm(r.opens_at), closes_at: hhmm(r.closes_at) }))
            .filter((r) => r.opens_at && r.closes_at)
          : []
        return { weekday, ranges }
      })
      const res = await updateBusinessHours(effective.slug, {
        timezone: hoursTimezone.trim(),
        hours,
      })
      const tz = String(res.timezone || hoursTimezone || 'UTC')
      const editor = hoursEditorFromResponse(res)
      setHoursTimezone(tz)
      setHoursDraft(editor)
      setHoursLoadedSnapshot(hoursSnapshot(tz, editor))
      setHoursSuccess('Hours saved')
      setAllBusinesses((rows) => rows.map((b) => (b.slug === effective.slug ? { ...b, timezone: tz } : b)))
      setPublicMerged((prev) => (prev && prev.slug === effective.slug ? { ...prev, timezone: tz } : prev))
    } catch (e: any) {
      setHoursError(e?.response?.data?.detail || e?.message || 'Failed to save hours.')
    } finally {
      setHoursSaving(false)
    }
  }

  const onSaveSpecialHour = async () => {
    if (!effective || !specialDate || specialHourSaving) return
    if (!specialIsClosed && (!specialOpensAt || !specialClosesAt || specialClosesAt <= specialOpensAt)) {
      setSpecialHoursError('When open, closes time must be after opens time.')
      return
    }
    setSpecialHourSaving(true)
    setSpecialHoursError(null)
    setHoursSuccess(null)
    try {
      await upsertBusinessSpecialHour(effective.slug, {
        date: specialDate,
        is_closed: specialIsClosed,
        opens_at: specialIsClosed ? null : specialOpensAt,
        closes_at: specialIsClosed ? null : specialClosesAt,
        note: specialNote.trim(),
      })
      const rows = await getBusinessSpecialHoursManage(effective.slug)
      setSpecialHours(rows)
      setHoursSuccess('Special day saved')
    } catch (e: any) {
      setSpecialHoursError(e?.response?.data?.detail || e?.message || 'Failed to save special day.')
    } finally {
      setSpecialHourSaving(false)
    }
  }

  const onDeleteSpecialHour = async (id: number) => {
    if (!effective || specialHourDeletingId != null) return
    setSpecialHourDeletingId(id)
    setSpecialHoursError(null)
    setHoursSuccess(null)
    try {
      await deleteBusinessSpecialHour(effective.slug, id)
      const rows = await getBusinessSpecialHoursManage(effective.slug)
      setSpecialHours(rows)
      setHoursSuccess('Special day removed')
    } catch (e: any) {
      setSpecialHoursError(e?.response?.data?.detail || e?.message || 'Failed to remove special day.')
    } finally {
      setSpecialHourDeletingId(null)
    }
  }

  const resetOfferForm = () => {
    setOfferEditId(null)
    setOfferModalOpen(false)
    setOfferForm({
      title: '',
      body: '',
      cta_type: 'CALL',
      cta_url: '',
      image_url: '',
      starts_at: '',
      ends_at: '',
      is_active: true,
    })
  }

  const onEditOffer = (offer: BusinessOffer) => {
    setOfferEditId(offer.id)
    setOfferModalOpen(true)
    setOfferForm({
      title: offer.title || '',
      body: offer.body || '',
      cta_type: offer.cta_type,
      cta_url: offer.cta_url || '',
      image_url: offer.image_url || '',
      starts_at: toDateTimeLocalValue(offer.starts_at),
      ends_at: toDateTimeLocalValue(offer.ends_at),
      is_active: Boolean(offer.is_active),
    })
    setOffersError(null)
    setOffersSuccess(null)
  }

  const onCreateOrUpdateOffer = async () => {
    if (!effective || offerSaving || !offerFormValid) return
    setOfferSaving(true)
    setOffersError(null)
    setOffersSuccess(null)
    try {
      const payload = {
        title: offerForm.title.trim(),
        body: offerForm.body.trim(),
        cta_type: offerForm.cta_type,
        cta_url: offerForm.cta_url.trim(),
        image_url: offerForm.image_url.trim(),
        starts_at: fromDateTimeLocalValue(offerForm.starts_at),
        ends_at: fromDateTimeLocalValue(offerForm.ends_at),
        is_active: offerForm.is_active,
      }
      if (offerEditId) {
        await updateBusinessOffer(effective.slug, offerEditId, payload)
        setOffersSuccess('Offer updated')
      } else {
        await createBusinessOffer(effective.slug, payload)
        setOffersSuccess('Offer created')
      }
      const rows = await listBusinessOffersManage(effective.slug)
      setOffersList(rows)
      resetOfferForm()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      if (detail) setOffersError(String(detail))
      else {
        const first = e?.response?.data && typeof e.response.data === 'object' ? Object.values(e.response.data)[0] : null
        if (Array.isArray(first) && first.length) setOffersError(String(first[0]))
        else if (typeof first === 'string') setOffersError(first)
        else setOffersError(e?.message || 'Failed to save offer.')
      }
    } finally {
      setOfferSaving(false)
    }
  }

  const onDeleteOffer = async (offer: BusinessOffer) => {
    if (!effective || offerDeletingId != null) return
    const ok = window.confirm(`Delete offer "${offer.title}"? This cannot be undone.`)
    if (!ok) return
    setOfferDeletingId(offer.id)
    setOffersError(null)
    setOffersSuccess(null)
    try {
      await deleteBusinessOffer(effective.slug, offer.id)
      const rows = await listBusinessOffersManage(effective.slug)
      setOffersList(rows)
      if (offerEditId === offer.id) resetOfferForm()
      setOffersSuccess('Offer deleted')
    } catch (e: any) {
      setOffersError(e?.response?.data?.detail || e?.message || 'Failed to delete offer.')
    } finally {
      setOfferDeletingId(null)
    }
  }

  const onToggleOfferActive = async (offer: BusinessOffer) => {
    if (!effective || offerTogglingId != null) return
    setOfferTogglingId(offer.id)
    setOffersError(null)
    setOffersSuccess(null)
    try {
      await updateBusinessOffer(effective.slug, offer.id, { is_active: !offer.is_active })
      const rows = await listBusinessOffersManage(effective.slug)
      setOffersList(rows)
      setOffersSuccess(offer.is_active ? 'Offer disabled' : 'Offer enabled')
    } catch (e: any) {
      setOffersError(e?.response?.data?.detail || e?.message || 'Failed to update offer status.')
    } finally {
      setOfferTogglingId(null)
    }
  }

  const onSaveBookingSettings = async () => {
    if (!effective?.slug || bookingSaving) return
    const duration = Number(bookingForm.slot_duration_minutes)
    const buffer = Number(bookingForm.buffer_minutes)
    const lead = Number(bookingForm.lead_time_minutes)
    const windowDays = Number(bookingForm.booking_window_days)
    const cancelCutoff = Number(bookingForm.cancel_cutoff_minutes)
    const rescheduleCutoff = Number(bookingForm.reschedule_cutoff_minutes)
    const maxPerDayRaw = bookingForm.max_bookings_per_day.trim()
    const maxPerDay = maxPerDayRaw ? Number(maxPerDayRaw) : null
    const depositAmountRaw = bookingForm.deposit_amount_cents.trim()
    const depositAmount = depositAmountRaw ? Number(depositAmountRaw) : null
    const depositPercentRaw = bookingForm.deposit_percent_bps.trim()
    const depositPercent = depositPercentRaw ? Number(depositPercentRaw) : null
    const normalizedTimezone = bookingForm.timezone.trim()
    if (!normalizedTimezone) {
      setBookingError('Timezone is required.')
      return
    }
    if (!isValidTimeZoneId(normalizedTimezone)) {
      setBookingError('Invalid timezone.')
      return
    }
    if (!Number.isFinite(duration) || duration < 5 || duration > 480) {
      setBookingError('Slot duration must be between 5 and 480 minutes.')
      return
    }
    if (!Number.isFinite(buffer) || buffer < 0 || buffer > 240) {
      setBookingError('Buffer minutes must be between 0 and 240.')
      return
    }
    if (!Number.isFinite(lead) || lead < 0 || lead > 10080) {
      setBookingError('Lead time must be between 0 and 10080 minutes.')
      return
    }
    if (!Number.isFinite(windowDays) || windowDays < 1 || windowDays > 365) {
      setBookingError('Booking window must be between 1 and 365 days.')
      return
    }
    if (!Number.isFinite(cancelCutoff) || cancelCutoff < 0 || cancelCutoff > 43200) {
      setBookingError('Cancel cutoff must be between 0 and 43200 minutes.')
      return
    }
    if (!Number.isFinite(rescheduleCutoff) || rescheduleCutoff < 0 || rescheduleCutoff > 43200) {
      setBookingError('Reschedule cutoff must be between 0 and 43200 minutes.')
      return
    }
    if (maxPerDayRaw && (!Number.isFinite(maxPerDay) || (maxPerDay as number) < 1)) {
      setBookingError('Max bookings per day must be at least 1 when set.')
      return
    }
    if (bookingForm.requires_deposit) {
      if (bookingForm.deposit_type === 'FIXED' && (!Number.isFinite(depositAmount) || (depositAmount as number) < 1)) {
        setBookingError('Fixed deposit amount (cents) is required.')
        return
      }
      if (bookingForm.deposit_type === 'PERCENT' && (!Number.isFinite(depositPercent) || (depositPercent as number) < 1 || (depositPercent as number) > 10000)) {
        setBookingError('Deposit percent (bps) must be between 1 and 10000.')
        return
      }
      if (!bookingStripe?.account_id) {
        setBookingError('Connect Stripe before enabling deposits.')
        return
      }
    }

    setBookingSaving(true)
    setBookingError(null)
    setBookingSuccess(null)
    try {
      const next = await updateBusinessBookingSettings(effective.slug, {
        timezone: normalizedTimezone,
        requires_deposit: bookingForm.requires_deposit,
        deposit_type: bookingForm.requires_deposit ? bookingForm.deposit_type : 'NONE',
        deposit_amount_cents: bookingForm.requires_deposit && bookingForm.deposit_type === 'FIXED' ? (depositAmount as number) : null,
        deposit_percent_bps: bookingForm.requires_deposit && bookingForm.deposit_type === 'PERCENT' ? (depositPercent as number) : null,
        currency: (bookingForm.currency || 'usd').toLowerCase(),
        slot_duration_minutes: duration,
        buffer_minutes: buffer,
        lead_time_minutes: lead,
        booking_window_days: windowDays,
        max_bookings_per_day: maxPerDay as number | null,
        allow_customer_cancel: bookingForm.allow_customer_cancel,
        allow_customer_reschedule: bookingForm.allow_customer_reschedule,
        cancel_cutoff_minutes: cancelCutoff,
        reschedule_cutoff_minutes: rescheduleCutoff,
        is_enabled: bookingForm.is_enabled,
      })
      setBookingSettings(next)
      const nextForm: BookingSettingsFormState = {
        timezone: next.timezone || 'UTC',
        requires_deposit: Boolean(next.requires_deposit),
        deposit_type: (next.deposit_type || 'NONE') as 'NONE' | 'FIXED' | 'PERCENT',
        deposit_amount_cents: next.deposit_amount_cents == null ? '' : String(next.deposit_amount_cents),
        deposit_percent_bps: next.deposit_percent_bps == null ? '' : String(next.deposit_percent_bps),
        currency: (next.currency || 'usd').toLowerCase(),
        slot_duration_minutes: String(next.slot_duration_minutes ?? 30),
        buffer_minutes: String(next.buffer_minutes ?? 0),
        lead_time_minutes: String(next.lead_time_minutes ?? 120),
        booking_window_days: String(next.booking_window_days ?? 30),
        max_bookings_per_day: next.max_bookings_per_day == null ? '' : String(next.max_bookings_per_day),
        allow_customer_cancel: next.allow_customer_cancel !== false,
        allow_customer_reschedule: next.allow_customer_reschedule !== false,
        cancel_cutoff_minutes: String(next.cancel_cutoff_minutes ?? 1440),
        reschedule_cutoff_minutes: String(next.reschedule_cutoff_minutes ?? 1440),
        is_enabled: Boolean(next.is_enabled),
      }
      setBookingForm(nextForm)
      setBookingLoadedSnapshot(bookingFormSnapshot(nextForm))
      const upcoming = await listBusinessUpcomingBookings(effective.slug, 30)
      setBookingUpcoming(upcoming)
      setBookingSuccess('Booking settings saved')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to save booking settings.')
    } finally {
      setBookingSaving(false)
    }
  }

  const onUseBrowserTimezone = () => {
    const browserTz = String(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC').trim() || 'UTC'
    setBookingForm((prev) => ({ ...prev, timezone: browserTz }))
    setBookingError(null)
    setBookingSuccess(null)
  }

  const onCancelBooking = async (booking: BusinessBookingItem) => {
    if (!effective?.slug || bookingCancelingId === booking.public_id) return
    if (!window.confirm(`Cancel booking for ${booking.customer_name || booking.customer_email}?`)) return
    const reason = window.prompt('Cancel reason (optional):', '') || ''
    setBookingCancelingId(booking.public_id)
    setBookingError(null)
    setBookingSuccess(null)
    try {
      await cancelBusinessBookingWithReason(booking.public_id, reason.trim())
      const upcoming = await listBusinessUpcomingBookings(effective.slug, 30)
      setBookingUpcoming(upcoming)
      setBookingSuccess('Booking cancelled')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to cancel booking.')
    } finally {
      setBookingCancelingId(null)
    }
  }

  const onOpenRescheduleBooking = (booking: BusinessBookingItem) => {
    setBookingRescheduleTarget(booking)
    setBookingRescheduleDateTime(toDateTimeLocalValue(booking.starts_at))
  }

  const onSubmitRescheduleBooking = async () => {
    if (!effective?.slug || !bookingRescheduleTarget || bookingReschedulingId) return
    const nextIso = fromDateTimeLocalValue(bookingRescheduleDateTime)
    if (!nextIso) {
      setBookingError('Select a valid date and time to reschedule.')
      return
    }
    setBookingReschedulingId(bookingRescheduleTarget.public_id)
    setBookingError(null)
    setBookingSuccess(null)
    try {
      await rescheduleBusinessBooking(bookingRescheduleTarget.public_id, nextIso, bookingForm.timezone || 'UTC')
      const upcoming = await listBusinessUpcomingBookings(effective.slug, 30)
      setBookingUpcoming(upcoming)
      setBookingSuccess('Booking rescheduled')
      setBookingRescheduleTarget(null)
      setBookingRescheduleDateTime('')
    } catch (e: any) {
      const code = e?.response?.data?.code
      if (code === 'SLOT_TAKEN') setBookingError('That slot is unavailable. Choose another time.')
      else if (code === 'EXPIRED') setBookingError('This unpaid booking has expired. Ask customer to book again.')
      else setBookingError(e?.response?.data?.detail || e?.message || 'Failed to reschedule booking.')
    } finally {
      setBookingReschedulingId(null)
    }
  }

  const onRefundBooking = async (booking: BusinessBookingItem) => {
    if (bookingRefundingId === booking.public_id || !effective?.slug) return
    if (!window.confirm(`Issue full refund for ${booking.customer_name || booking.customer_email}?`)) return
    setBookingRefundingId(booking.public_id)
    setBookingError(null)
    setBookingSuccess(null)
    try {
      await refundBusinessBooking(booking.public_id)
      const upcoming = await listBusinessUpcomingBookings(effective.slug, 30)
      setBookingUpcoming(upcoming)
      setBookingSuccess('Refund requested. Status will update after webhook confirmation.')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to request refund.')
    } finally {
      setBookingRefundingId(null)
    }
  }

  const onResendBookingEmail = async (booking: BusinessBookingItem) => {
    if (!effective?.slug || bookingResendingId === booking.public_id) return
    setBookingError(null)
    setBookingSuccess(null)
    setBookingResendingId(booking.public_id)
    try {
      await resendBusinessBookingConfirm(booking.public_id)
      setBookingSuccess('Confirmation email requeued')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to resend confirmation email.')
    } finally {
      setBookingResendingId(null)
    }
  }

  const onDownloadBookingCalendar = async (booking: BusinessBookingItem) => {
    try {
      const kind = booking.status === 'CANCELLED' || booking.status === 'REFUNDED' ? 'cancel' : 'request'
      await downloadBusinessBookingIcs(booking.public_id, kind)
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to download calendar file.')
    }
  }

  const resetBookingServiceForm = () => {
    setBookingServiceForm({
      id: null,
      name: '',
      duration_minutes: '30',
      capacity: '1',
      price_cents: '',
      is_active: true,
    })
  }

  const onSaveBookingService = async () => {
    if (!effective?.slug || bookingServiceSaving) return
    const name = bookingServiceForm.name.trim()
    const duration = Number(bookingServiceForm.duration_minutes)
    const capacity = Number(bookingServiceForm.capacity)
    if (!name) {
      setBookingServiceError('Service name is required.')
      return
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      setBookingServiceError('Duration must be greater than 0.')
      return
    }
    if (!Number.isFinite(capacity) || !Number.isInteger(capacity) || capacity < 1) {
      setBookingServiceError('Capacity must be a whole number of at least 1.')
      return
    }
    setBookingServiceSaving(true)
    setBookingServiceError(null)
    try {
      if (bookingServiceForm.id) {
        await updateBusinessBookingService(effective.slug, bookingServiceForm.id, {
          name,
          duration_minutes: duration,
          capacity,
          price_cents: bookingServiceForm.price_cents.trim() ? Number(bookingServiceForm.price_cents) : null,
          is_active: bookingServiceForm.is_active,
        })
      } else {
        await createBusinessBookingService(effective.slug, {
          name,
          duration_minutes: duration,
          capacity,
          price_cents: bookingServiceForm.price_cents.trim() ? Number(bookingServiceForm.price_cents) : null,
          is_active: true,
        })
      }
      const services = await listBusinessBookingServices(effective.slug)
      setBookingServices(services)
      resetBookingServiceForm()
    } catch (e: any) {
      setBookingServiceError(e?.response?.data?.detail || e?.message || 'Failed to save service.')
    } finally {
      setBookingServiceSaving(false)
    }
  }

  const onEditBookingService = (service: BookingService) => {
    setBookingServiceError(null)
    setBookingServiceForm({
      id: service.id,
      name: service.name || '',
      duration_minutes: String(service.duration_minutes || 30),
      capacity: String(service.capacity || 1),
      price_cents: service.price_cents == null ? '' : String(service.price_cents),
      is_active: Boolean(service.is_active),
    })
  }

  const onDisableBookingService = async (service: BookingService) => {
    if (!effective?.slug || bookingServiceSaving) return
    if (!window.confirm(`Disable service "${service.name}"?`)) return
    setBookingServiceSaving(true)
    setBookingServiceError(null)
    try {
      await disableBusinessBookingService(effective.slug, service.id)
      const services = await listBusinessBookingServices(effective.slug)
      setBookingServices(services)
      if (bookingServiceForm.id === service.id) resetBookingServiceForm()
    } catch (e: any) {
      setBookingServiceError(e?.response?.data?.detail || e?.message || 'Failed to disable service.')
    } finally {
      setBookingServiceSaving(false)
    }
  }

  const onStartBookingStripeConnect = async () => {
    if (!effective?.slug || bookingStripeConnecting) return
    setBookingStripeConnecting(true)
    setBookingError(null)
    try {
      const res = await startBusinessStripeConnect(effective.slug)
      if (res.url) {
        window.location.href = res.url
        return
      }
      const status = await businessStripeConnectStatus(effective.slug)
      setBookingStripe(status)
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to start Stripe Connect.')
    } finally {
      setBookingStripeConnecting(false)
    }
  }

  const onConnectGoogleCalendar = async () => {
    if (!effective?.slug || bookingExternalConnecting) return
    setBookingExternalConnecting(true)
    setBookingError(null)
    try {
      const returnUrl = `${window.location.origin}/settings/pages/${encodeURIComponent(effective.slug)}?tab=booking`
      const oauthUrl = await startGoogleCalendarConnect({
        provider_type: 'BUSINESS',
        provider_id: Number(effective.id),
        provider_public_id: effective.slug,
        return_url: returnUrl,
      })
      if (!oauthUrl) throw new Error('Missing OAuth URL')
      window.location.assign(oauthUrl)
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to start Google Calendar connect.')
      setBookingExternalConnecting(false)
    }
  }

  const onSyncExternalCalendars = async () => {
    if (!effective?.slug || bookingExternalSyncing) return
    setBookingExternalSyncing(true)
    setBookingError(null)
    try {
      await syncBookingExternalCalendars({
        provider_type: 'BUSINESS',
        provider_public_id: effective.slug,
        days: 31,
      })
      const [connections, upcoming] = await Promise.all([
        listBookingExternalCalendars({ provider_type: 'BUSINESS', provider_public_id: effective.slug }),
        listBusinessUpcomingBookings(effective.slug, 30),
      ])
      setBookingExternalCalendars(connections)
      setBookingUpcoming(upcoming)
      setBookingSuccess('External calendar sync complete.')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to sync external calendars.')
    } finally {
      setBookingExternalSyncing(false)
    }
  }

  const onDisconnectExternalCalendar = async (row: ExternalCalendarConnection) => {
    if (bookingExternalDisconnectingId != null) return
    if (!window.confirm(`Disconnect ${row.kind === 'google' ? 'Google Calendar' : row.kind} for bookings?`)) return
    setBookingExternalDisconnectingId(row.id)
    setBookingError(null)
    try {
      const updated = await disconnectBookingExternalCalendar(row.id)
      setBookingExternalCalendars((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
      setBookingSuccess('External calendar disconnected.')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to disconnect external calendar.')
    } finally {
      setBookingExternalDisconnectingId(null)
    }
  }

  const onToggleBookingStaff = async (member: BookingStaffMember, field: 'is_active' | 'is_public', value: boolean) => {
    if (!effective?.slug) return
    try {
      const updated = await updateBusinessBookingStaff(effective.slug, member.id, { [field]: value })
      setBookingStaff((prev) => prev.map((row) => (row.id === member.id ? updated : row)))
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to update staff member.')
    }
  }

  const onAddBookingStaff = async () => {
    if (!effective?.slug || bookingStaffAdding || !bookingStaffLookup.trim()) return
    setBookingStaffAdding(true)
    setBookingError(null)
    try {
      const row = await addBusinessBookingStaff(effective.slug, {
        user_lookup: bookingStaffLookup.trim(),
        is_active: true,
        is_public: true,
      })
      setBookingStaff((prev) => {
        const deduped = prev.filter((item) => item.id !== row.id)
        return [...deduped, row].sort((a, b) => a.user.display_name.localeCompare(b.user.display_name))
      })
      setBookingStaffSelectedId(row.id)
      setBookingStaffLookup('')
      setBookingSuccess('Staff member added.')
    } catch (e: any) {
      setBookingError(e?.response?.data?.user_lookup || e?.response?.data?.detail || e?.message || 'Failed to add staff member.')
    } finally {
      setBookingStaffAdding(false)
    }
  }

  const onStaffScheduleToast = (kind: 'success' | 'error', label: string) => {
    if (kind === 'success') {
      setBookingSuccess(label)
      setBookingError(null)
      return
    }
    setBookingError(label)
  }

  const selectedBookingStaff = bookingStaff.find((row) => row.id === bookingStaffSelectedId) || null

  const onRemoveBookingStaff = async (member: BookingStaffMember) => {
    if (!effective?.slug) return
    if (!window.confirm(`Remove ${member.user.display_name} from booking staff?`)) return
    try {
      await removeBusinessBookingStaff(effective.slug, member.id)
      setBookingStaff((prev) => prev.filter((row) => row.id !== member.id))
      setBookingStaffSelectedId((prev) => (prev === member.id ? null : prev))
      setBookingSuccess('Staff member removed.')
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to remove staff member.')
    }
  }

  if (!authed) return null

  if (loading) {
    return <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">Loading business console...</div>
  }

  if (error) {
    return <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>
  }

  if (!mineBusiness) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <h1 className="text-xl font-extrabold">Not authorized</h1>
        <p className="mt-2 text-sm text-white/70">You do not have access to manage this business.</p>
        <div className="mt-3">
          <Link to="/settings/pages" className="inline-flex rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600">
            Back to Pixe Pages
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold">{effective?.name}</h1>
            <div className="mt-1 flex items-center gap-2 text-sm">
              <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5">{effective?.category}</span>
              <span className={`rounded-full border px-2 py-0.5 text-xs ${statusClass}`}>{statusLabel}</span>
              <span className="text-white/70">Reputation {rep}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedSlug}
              onChange={(e) => navigate(`/settings/pages/${e.target.value}?tab=${tab}`)}
              className="rounded-lg border border-white/15 bg-black/30 px-2 py-2 text-sm outline-none"
            >
              {allBusinesses.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.name} ({b.slug})
                </option>
              ))}
            </select>
            <Link to={`/b/${effective?.slug}`} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
              View Public Page
            </Link>
            <Link
              to={`/settings/pages/${effective?.slug}/edit`}
              className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              Edit Page
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'overview')}`} onClick={() => changeTab('overview')}>Overview</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'promotion')}`} onClick={() => changeTab('promotion')}>Promotion</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'offers')}`} onClick={() => changeTab('offers')}>Offers</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'ads')}`} onClick={() => changeTab('ads')}>Call Ads</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'calls')}`} onClick={() => changeTab('calls')}>Calls</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'team')}`} onClick={() => changeTab('team')}>Team</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'hours')}`} onClick={() => changeTab('hours')}>Hours</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'booking')}`} onClick={() => changeTab('booking')}>Booking</button>
        <button className={`rounded-lg border px-3 py-2 text-sm ${clsActive(tab === 'page_settings')}`} onClick={() => changeTab('page_settings')}>Page Settings</button>
      </div>

      {tab === 'overview' ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Status</div>
              <div className="mt-2 text-lg font-bold">{summaryStatusLabel}</div>
              <div className="text-xs text-white/55">{summaryStatusActive ? 'Visible in Discover.' : 'Not visible until setup fee is paid.'}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Reputation</div>
              <div className="mt-2 text-lg font-bold">{summaryRep}</div>
              <div className="text-xs text-white/55">Reputation updates as your page earns engagement.</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Members</div>
              <div className="mt-2 text-lg font-bold">{summaryMembers}</div>
              <div className="text-xs text-white/55">Includes owner and assigned managers/editors.</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Spend this month</div>
              <div className="mt-2 text-lg font-bold">{spendThisMonth} PIXE</div>
              <div className="text-xs text-white/55">Boost + Call click spend.</div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Next steps</div>
              <div className="text-xs text-white/60">{completionDoneCount}/{completionRows.length} complete</div>
            </div>
            <div className="mt-3 space-y-2">
              {completionRows.map((row) => (
                <div key={row.key} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-xs ${row.done ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' : 'border-white/20 text-white/60'}`}>
                      {row.done ? '✓' : '•'}
                    </span>
                    <span className={row.done ? 'text-white/85' : 'text-white/70'}>{row.label}</span>
                  </div>
                  {row.done ? <span className="text-xs text-emerald-300">Done</span> : <Link to={row.href} className="text-xs text-brand hover:underline">Add</Link>}
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Link to={`/settings/pages/${effective?.slug}?tab=promotion`} className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 hover:bg-white/10">Open Promotion</Link>
              <Link to={`/settings/pages/${effective?.slug}?tab=calls`} className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 hover:bg-white/10">Open Calls</Link>
            </div>
          </div>

          {bookingRescheduleTarget ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Reschedule booking</div>
                  <div className="text-xs text-white/65">
                    {bookingRescheduleTarget.customer_name || bookingRescheduleTarget.customer_email}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (bookingReschedulingId) return
                    setBookingRescheduleTarget(null)
                    setBookingRescheduleDateTime('')
                  }}
                  className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10"
                >
                  Close
                </button>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                <label className="text-sm">
                  <span className="text-white/75">New start</span>
                  <input
                    type="datetime-local"
                    value={bookingRescheduleDateTime}
                    onChange={(e) => setBookingRescheduleDateTime(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={onSubmitRescheduleBooking}
                    disabled={!bookingRescheduleDateTime || Boolean(bookingReschedulingId)}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold ${!bookingRescheduleDateTime || Boolean(bookingReschedulingId) ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                  >
                    {bookingReschedulingId ? 'Saving...' : 'Save reschedule'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Performance (last 30 days)</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-xs text-white/60">Boosted placements active</div>
                <div className="mt-1 text-lg font-bold">{overviewSummary?.boosts.active_boosts_count ?? 0}</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-xs text-white/60">Call clicks</div>
                <div className="mt-1 text-lg font-bold">{overviewSummary?.calls.call_clicks_total_30d ?? 0}</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-xs text-white/60">Charged clicks</div>
                <div className="mt-1 text-lg font-bold">{overviewSummary?.calls.call_clicks_charged_30d ?? 0}</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-xs text-white/60">Spend (PIXE)</div>
                <div className="mt-1 text-lg font-bold">{overviewSummary?.spend_total_30d ?? '0.00'}</div>
              </div>
            </div>
            <div className="mt-3 text-sm text-white/75">Page age: {summaryAgeDays} day(s)</div>
            <div className="mt-2 text-sm text-white/75">
              Spend split (30d): Organic 0.00 PIXE / Boost {overviewSummary?.spend_breakdown.boosts ?? '0.00'} PIXE / Calls {overviewSummary?.spend_breakdown.calls ?? '0.00'} PIXE
            </div>
            {overviewLoading ? <div className="mt-2 text-xs text-white/60">Loading summary...</div> : null}
            {overviewError ? <div className="mt-2 text-xs text-red-300">{overviewError}</div> : null}
          </div>
        </div>
      ) : null}

      {tab === 'promotion' ? (
        <div className="space-y-4">
          {activeBoost ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Active boost</div>
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-emerald-200">Active</span>
                    <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5">{activeBoost.scope}</span>
                  </div>
                  <div className="mt-2 text-xs text-white/70">
                    Budget {activeBoost.budget_pixe} PIXE | {new Date(activeBoost.starts_at).toLocaleString()} - {new Date(activeBoost.ends_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onCancelBoost(activeBoost.id)}
                  disabled={cancelingBoostId === activeBoost.id}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${cancelingBoostId === activeBoost.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                >
                  {cancelingBoostId === activeBoost.id ? 'Stopping...' : 'Stop Boost'}
                </button>
              </div>
              <div className="mt-2 text-xs text-white/60">No refunds in v1 when stopping a boost.</div>
            </div>
          ) : null}

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-lg font-extrabold">Boost your listing</div>
            <div className="mt-1 text-sm text-white/70">Create a paid boost campaign.</div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div>
                <label className="text-xs text-white/60">Budget (PIXE)</label>
                <input value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {!budgetValid ? <div className="mt-1 text-xs text-amber-200">Enter a positive number.</div> : null}
              </div>
              <div>
                <label className="text-xs text-white/60">Duration</label>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                {!durationValid ? <div className="mt-1 text-xs text-amber-200">Duration must be between 1 and 30 days.</div> : null}
              </div>
              <div>
                <label className="text-xs text-white/60">Scope</label>
                <select value={scope} onChange={(e) => setScope(e.target.value as any)} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none">
                  <option value="LOCAL">Local radius</option>
                  <option value="CATEGORY">Category</option>
                  <option value="SEARCH">Search</option>
                </select>
              </div>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs text-white/60">Category (for CATEGORY scope)</label>
                <input
                  value={scopeCategory}
                  onChange={(e) => setScopeCategory(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  placeholder="Category"
                />
                {!categoryValid ? <div className="mt-1 text-xs text-amber-200">Category is required for CATEGORY scope.</div> : null}
              </div>
              <div>
                <label className="text-xs text-white/60">Radius miles (for LOCAL scope)</label>
                <input
                  value={scopeRadius}
                  onChange={(e) => setScopeRadius(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  placeholder="10"
                />
                {!radiusValid ? <div className="mt-1 text-xs text-amber-200">Radius is required for LOCAL scope.</div> : null}
              </div>
            </div>
            <div className="mt-4">
              <button
                type="button"
                disabled={!budgetValid || !durationValid || !radiusValid || !categoryValid || boostSaving}
                onClick={onCreateBoost}
                className={`rounded-lg px-4 py-2 text-sm font-semibold border border-white/10 ${!budgetValid || !durationValid || !radiusValid || !categoryValid || boostSaving ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {boostSaving ? 'Starting Boost...' : 'Start Boost'}
              </button>
            </div>
            <div className="mt-2 text-xs text-white/60">Boosted placements are always labeled.</div>
            <div className="mt-1 text-xs text-white/60">No refunds in v1 when stopping a boost.</div>
            {boostSuccess ? <div className="mt-2 text-xs text-emerald-300">{boostSuccess}</div> : null}
            {boostError ? <div className="mt-2 text-xs text-red-300">{boostError}</div> : null}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm font-semibold">Charges</div>
              <div className="mt-2 text-sm text-white/80">Boost spend (30d): <span className="font-semibold">{boostSpend30d} PIXE</span></div>
              <div className="mt-1 text-sm text-white/80">Call spend (30d): <span className="font-semibold">{callSpend30d} PIXE</span></div>
              <div className="mt-1 text-sm text-white/80">This month spend: <span className="font-semibold">{overviewSummary?.spend_this_month_pixe ?? '0.00'} PIXE</span></div>
              <div className="mt-2 text-xs text-white/55">Charges are deducted from the owner wallet.</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm font-semibold">History</div>
              {boostsLoading ? <div className="mt-2 text-sm text-white/70">Loading boost history...</div> : null}
              {!boostsLoading && boostHistory.length === 0 ? <div className="mt-2 text-sm text-white/70">No boosted campaigns yet.</div> : null}
              {!boostsLoading && boostHistory.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {boostHistory.map((b) => (
                    <div key={b.id} className="rounded-lg border border-white/10 bg-black/20 p-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span>{b.scope}</span>
                          <span className={`rounded-full border px-2 py-0.5 ${isBoostActiveNow(b) ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' : !b.is_active ? 'border-amber-500/30 bg-amber-500/10 text-amber-200' : 'border-white/15 bg-white/5 text-white/70'}`}>
                            {isBoostActiveNow(b) ? 'Active' : !b.is_active ? 'Canceled' : 'Ended'}
                          </span>
                        </div>
                        {isBoostActiveNow(b) ? (
                          <button
                            type="button"
                            onClick={() => onCancelBoost(b.id)}
                            disabled={cancelingBoostId === b.id}
                            className={`rounded-md px-2 py-1 text-[11px] ${cancelingBoostId === b.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                          >
                            {cancelingBoostId === b.id ? 'Stopping...' : 'Stop'}
                          </button>
                        ) : null}
                      </div>
                      <div className="mt-1 text-white/70">
                        Budget {b.budget_pixe} PIXE | Created {new Date(b.created_at).toLocaleString()} | Duration {Math.max(1, Math.ceil((new Date(b.ends_at).getTime() - new Date(b.starts_at).getTime()) / 86400000))}d
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'offers' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-lg font-extrabold">Create offer</div>
                <div className="mt-1 text-sm text-white/70">Offers are promotional cards shown on your public page.</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs text-emerald-200">
                  {activeOffersCount} active
                </span>
                <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-white/80">
                  {offersList.length} total
                </span>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs text-white/60">Title</label>
                <input
                  value={offerForm.title}
                  onChange={(e) => setOfferForm((p) => ({ ...p, title: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  placeholder="Offer title"
                />
                {!offerHasTitle ? <div className="mt-1 text-xs text-amber-200">Title is required.</div> : null}
              </div>
              <div>
                <label className="text-xs text-white/60">CTA type</label>
                <select
                  value={offerForm.cta_type}
                  onChange={(e) => setOfferForm((p) => ({ ...p, cta_type: e.target.value as 'CALL' | 'WEBSITE' | 'LINK' | 'NONE' }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none"
                >
                  <option value="CALL">Call</option>
                  <option value="WEBSITE">Website</option>
                  <option value="LINK">Link</option>
                  <option value="NONE">None</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-white/60">Body</label>
                <textarea
                  value={offerForm.body}
                  onChange={(e) => setOfferForm((p) => ({ ...p, body: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  placeholder="Offer details"
                />
              </div>
              {offerNeedsUrl || offerWebsiteNeedsUrl ? (
                <div>
                  <label className="text-xs text-white/60">
                    {offerForm.cta_type === 'LINK' ? 'Custom link URL' : 'Website override URL (optional)'}
                  </label>
                  <input
                    value={offerForm.cta_url}
                    onChange={(e) => setOfferForm((p) => ({ ...p, cta_url: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    placeholder="https://example.com"
                  />
                  {offerForm.cta_type === 'WEBSITE' ? (
                    <div className="mt-1 text-xs text-white/60">Uses your business website by default when blank.</div>
                  ) : null}
                  {!offerHasRequiredUrl ? <div className="mt-1 text-xs text-amber-200">CTA URL is required for Link offers.</div> : null}
                  {!offerUrlFormatValid ? <div className="mt-1 text-xs text-amber-200">CTA URL must be a valid http(s) URL.</div> : null}
                </div>
              ) : null}
              <OfferImageUploader
                value={offerForm.image_url}
                onChange={(url) => setOfferForm((p) => ({ ...p, image_url: url }))}
                label="Offer image (optional)"
                onUploaded={() => setOffersSuccess('Offer image uploaded')}
                onError={(message) => setOffersError(message)}
              />
              <div>
                <label className="text-xs text-white/60">Starts at (optional)</label>
                <input
                  type="datetime-local"
                  value={offerForm.starts_at}
                  onChange={(e) => setOfferForm((p) => ({ ...p, starts_at: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div>
                <label className="text-xs text-white/60">Ends at (optional)</label>
                <input
                  type="datetime-local"
                  value={offerForm.ends_at}
                  onChange={(e) => setOfferForm((p) => ({ ...p, ends_at: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                {!offerDateWindowValid ? <div className="mt-1 text-xs text-amber-200">End must be after start.</div> : null}
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input
                    type="checkbox"
                    checked={offerForm.is_active}
                    onChange={(e) => setOfferForm((p) => ({ ...p, is_active: e.target.checked }))}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onCreateOrUpdateOffer}
                disabled={!offerFormValid || offerSaving}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${!offerFormValid || offerSaving ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {offerSaving ? 'Saving...' : 'Create Offer'}
              </button>
            </div>
            {offersSuccess ? <div className="mt-2 text-xs text-emerald-300">{offersSuccess}</div> : null}
            {offersError ? <div className="mt-2 text-xs text-red-300">{offersError}</div> : null}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Your offers</div>
            {offersLoading ? <div className="mt-2 text-sm text-white/70">Loading offers...</div> : null}
            {!offersLoading && offersList.length === 0 ? <div className="mt-2 text-sm text-white/70">No offers yet.</div> : null}
            {!offersLoading && offersList.length > 0 ? (
              <div className="mt-3 space-y-2">
                {offersList.map((offer) => {
                  const statusUi = offerStatusUi(offer)
                  return (
                    <div key={offer.id} className="rounded-lg border border-white/10 bg-black/20 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="font-semibold text-white">{offer.title}</div>
                          <div className="mt-1 text-xs text-white/65">
                            {offer.starts_at ? new Date(offer.starts_at).toLocaleString() : 'Now'} - {offer.ends_at ? new Date(offer.ends_at).toLocaleString() : 'No end'}
                          </div>
                        </div>
                        <span className={`rounded-full border px-2 py-0.5 text-xs ${statusUi.className}`}>{statusUi.label}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEditOffer(offer)}
                          className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleOfferActive(offer)}
                          disabled={offerTogglingId === offer.id}
                          className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${offerTogglingId === offer.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white/85 hover:bg-white/10'}`}
                        >
                          {offerTogglingId === offer.id ? 'Updating...' : offer.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteOffer(offer)}
                          disabled={offerDeletingId === offer.id}
                          className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${offerDeletingId === offer.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                        >
                          {offerDeletingId === offer.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>

          {offerModalOpen ? (
            <div className="fixed inset-0 z-50">
              <button
                type="button"
                aria-label="Close edit offer modal"
                className="absolute inset-0 bg-black/70"
                onClick={() => !offerSaving && resetOfferForm()}
              />
              <div className="absolute inset-x-4 top-16 mx-auto max-w-2xl rounded-2xl border border-white/10 bg-neutral-950 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-extrabold">Edit offer</h3>
                  <button type="button" onClick={() => !offerSaving && resetOfferForm()} className="text-sm text-white/70 hover:text-white">
                    Close
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-xs text-white/60">Title</label>
                    <input
                      value={offerForm.title}
                      onChange={(e) => setOfferForm((p) => ({ ...p, title: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                      placeholder="Offer title"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60">CTA type</label>
                    <select
                      value={offerForm.cta_type}
                      onChange={(e) => setOfferForm((p) => ({ ...p, cta_type: e.target.value as 'CALL' | 'WEBSITE' | 'LINK' | 'NONE' }))}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none"
                    >
                      <option value="CALL">Call</option>
                      <option value="WEBSITE">Website</option>
                      <option value="LINK">Link</option>
                      <option value="NONE">None</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-white/60">Body</label>
                    <textarea
                      value={offerForm.body}
                      onChange={(e) => setOfferForm((p) => ({ ...p, body: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                  </div>
                  {offerNeedsUrl || offerWebsiteNeedsUrl ? (
                    <div>
                      <label className="text-xs text-white/60">
                        {offerForm.cta_type === 'LINK' ? 'Custom link URL' : 'Website override URL (optional)'}
                      </label>
                      <input
                        value={offerForm.cta_url}
                        onChange={(e) => setOfferForm((p) => ({ ...p, cta_url: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                        placeholder="https://example.com"
                      />
                      {!offerHasRequiredUrl ? <div className="mt-1 text-xs text-amber-200">CTA URL is required for Link offers.</div> : null}
                      {!offerUrlFormatValid ? <div className="mt-1 text-xs text-amber-200">CTA URL must be a valid http(s) URL.</div> : null}
                    </div>
                  ) : null}
                  <OfferImageUploader
                    value={offerForm.image_url}
                    onChange={(url) => setOfferForm((p) => ({ ...p, image_url: url }))}
                    label="Offer image (optional)"
                    onUploaded={() => setOffersSuccess('Offer image uploaded')}
                    onError={(message) => setOffersError(message)}
                  />
                  <div>
                    <label className="text-xs text-white/60">Starts at (optional)</label>
                    <input
                      type="datetime-local"
                      value={offerForm.starts_at}
                      onChange={(e) => setOfferForm((p) => ({ ...p, starts_at: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60">Ends at (optional)</label>
                    <input
                      type="datetime-local"
                      value={offerForm.ends_at}
                      onChange={(e) => setOfferForm((p) => ({ ...p, ends_at: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm text-white/80">
                      <input
                        type="checkbox"
                        checked={offerForm.is_active}
                        onChange={(e) => setOfferForm((p) => ({ ...p, is_active: e.target.checked }))}
                      />
                      Active
                    </label>
                  </div>
                </div>

                {!offerHasTitle ? <div className="mt-2 text-xs text-amber-200">Title is required.</div> : null}
                {!offerDateWindowValid ? <div className="mt-1 text-xs text-amber-200">End must be after start.</div> : null}
                {offersError ? <div className="mt-1 text-xs text-red-300">{offersError}</div> : null}

                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={resetOfferForm}
                    disabled={offerSaving}
                    className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onCreateOrUpdateOffer}
                    disabled={!offerFormValid || offerSaving || !offerEditId}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold ${!offerFormValid || offerSaving || !offerEditId ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                  >
                    {offerSaving ? 'Saving...' : 'Save Offer'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === 'ads' ? (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Status</div>
              <div className="mt-2 flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-xs ${adsStatusClass}`}>{adsStatusLabel}</span>
              </div>
              <div className="mt-2 space-y-1">
                {(adsSummary?.ineligible_reasons || []).map((reason) => {
                  const row = callAdsReasonUi(reason, effective?.slug || '')
                  return (
                    <div key={reason} className="text-xs text-white/70">
                      {row.label}{' '}
                      {row.href ? <Link to={row.href} className="text-brand hover:underline">Fix</Link> : null}
                    </div>
                  )
                })}
                {adsSummary && (adsSummary.ineligible_reasons || []).length === 0 ? (
                  <div className="text-xs text-emerald-200">Campaign is eligible for call-ad auctions.</div>
                ) : null}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Today budget</div>
              <div className="mt-2 text-sm text-white/80">Spent today: <span className="font-semibold">{adsSummary?.spent_today_pixe ?? '0.00'} PIXE</span></div>
              <div className="mt-1 text-sm text-white/80">Remaining today: <span className="font-semibold">{adsSummary?.remaining_today_pixe ?? '0.00'} PIXE</span></div>
              <div className="mt-1 text-sm text-white/80">Daily budget: <span className="font-semibold">{adsSummary?.campaign?.daily_budget_pixe ?? '0.00'} PIXE</span></div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">30-day performance</div>
              <div className="mt-2 text-sm text-white/80">Sponsored leads: <span className="font-semibold">{adsSummary?.sponsored_leads_total ?? 0}</span></div>
              <div className="mt-1 text-sm text-white/80">Sponsored spend: <span className="font-semibold">{adsSummary?.sponsored_spend_pixe ?? '0.00'} PIXE</span></div>
              <div className="mt-1 text-sm text-white/80">Avg CPL: <span className="font-semibold">{adsSummary?.avg_cpl_sponsored ?? '0.00'} PIXE</span></div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-lg font-extrabold">Call Ads Campaign</div>
            <div className="mt-1 text-sm text-white/70">You're charged when a user clicks Call. Sponsored placements are labeled.</div>

            {adsLoading ? <div className="mt-3 text-sm text-white/70">Loading campaign...</div> : null}

            {!adsLoading ? (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs text-white/60">Status</label>
                  <select
                    value={adsForm.status}
                    onChange={(e) => setAdsForm((p) => ({ ...p, status: e.target.value as 'ACTIVE' | 'PAUSED' }))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="PAUSED">Paused</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/60">Max CPC (PIXE)</label>
                  <input
                    value={adsForm.max_cpc_pixe}
                    onChange={(e) => setAdsForm((p) => ({ ...p, max_cpc_pixe: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                  {!adsMaxCpcValid ? <div className="mt-1 text-xs text-amber-200">Must be greater than 0.</div> : null}
                </div>
                <div>
                  <label className="text-xs text-white/60">Daily budget (PIXE)</label>
                  <input
                    value={adsForm.daily_budget_pixe}
                    onChange={(e) => setAdsForm((p) => ({ ...p, daily_budget_pixe: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                  {!adsDailyBudgetValid ? <div className="mt-1 text-xs text-amber-200">Must be &gt;= Max CPC.</div> : null}
                </div>
                <div>
                  <label className="text-xs text-white/60">Radius miles</label>
                  <input
                    value={adsForm.radius_miles}
                    onChange={(e) => setAdsForm((p) => ({ ...p, radius_miles: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                  {!adsRadiusValid ? <div className="mt-1 text-xs text-amber-200">Radius is required and must be &gt; 0.</div> : null}
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-white/60">Categories (comma separated)</label>
                  <input
                    value={adsForm.categoriesText}
                    onChange={(e) => setAdsForm((p) => ({ ...p, categoriesText: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    placeholder="telecommunications, services"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {CALL_ADS_CATEGORY_SUGGESTIONS.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          const existing = parseCategoriesInput(adsForm.categoriesText)
                          if (existing.includes(cat)) return
                          const next = [...existing, cat].join(', ')
                          setAdsForm((p) => ({ ...p, categoriesText: next }))
                        }}
                        className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] text-white/75 hover:bg-white/10"
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  {!adsCategoriesValid ? <div className="mt-1 text-xs text-amber-200">Add at least one category.</div> : null}
                </div>
              </div>
            ) : null}

            <div className="mt-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={onSaveCallAdsCampaign}
                  disabled={!adsFormValid || !adsDirty || adsSaving || adsLoading}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${!adsFormValid || !adsDirty || adsSaving || adsLoading ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                >
                  {adsSaving ? 'Saving...' : callAdsCampaign ? 'Save Campaign' : 'Create Call Ads Campaign'}
                </button>
                {callAdsCampaign ? (
                  <button
                    type="button"
                    onClick={() => onToggleCallAdsStatus(callAdsCampaign.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE')}
                    disabled={adsSaving || adsLoading}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold ${adsSaving || adsLoading ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/20 bg-white/5 text-white/85 hover:bg-white/10'}`}
                  >
                    {callAdsCampaign.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-2 text-xs text-white/60">Charges occur on call click (lead). No refunds in v1.</div>
            {adsSuccess ? <div className="mt-2 text-xs text-emerald-300">{adsSuccess}</div> : null}
            {adsError ? <div className="mt-2 text-xs text-red-300">{adsError}</div> : null}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Recent sponsored leads</div>
              <button
                type="button"
                onClick={onExportCallAdsCsv}
                disabled={adsExporting}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${adsExporting ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
              >
                {adsExporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>

            {adsEvents.length === 0 && !adsLoading ? <div className="mt-3 text-sm text-white/70">No sponsored call leads yet.</div> : null}
            {adsEvents.length > 0 ? (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="text-white/60">
                    <tr>
                      <th className="px-2 py-2 font-medium">Time</th>
                      <th className="px-2 py-2 font-medium">Source</th>
                      <th className="px-2 py-2 font-medium">Who</th>
                      <th className="px-2 py-2 font-medium">Status</th>
                      <th className="px-2 py-2 font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adsEvents.map((evt) => {
                      const statusUi = callStatusUi(evt.charge_status)
                      return (
                        <tr key={evt.id} className="border-t border-white/10">
                          <td className="px-2 py-2 text-white/80" title={new Date(evt.created_at).toLocaleString()}>
                            {relativeTimeFromNow(evt.created_at)}
                          </td>
                          <td className="px-2 py-2 text-white/80">{evt.source === 'READER' ? 'Reader' : evt.source === 'CARD' ? 'Card' : 'Other'}</td>
                          <td className="px-2 py-2 text-white/80">{evt.actor?.handle || evt.actor?.username || 'Guest'}</td>
                          <td className="px-2 py-2">
                            <span className={`rounded-full border px-2 py-0.5 ${statusUi.className}`}>{statusUi.label}</span>
                          </td>
                          <td className="px-2 py-2 text-white/80">{evt.charged_pixe} PIXE</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
            {adsEventsHasNext ? (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={onLoadMoreAdsEvents}
                  disabled={adsEventsLoadingMore}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold ${adsEventsLoadingMore ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
                >
                  {adsEventsLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            ) : null}
            {adsEventsError ? <div className="mt-2 text-xs text-red-300">{adsEventsError}</div> : null}
          </div>
        </div>
      ) : null}

      {tab === 'calls' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-1">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setCallsSinceDays(d as 7 | 30 | 90)}
                    className={`rounded-md px-3 py-1.5 text-xs ${callsSinceDays === d ? 'bg-brand text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${billingStatusClass}`}>
                {billingActive ? 'Billing active' : 'Billing inactive'}
              </span>
            </div>
          </div>
          {callSettings?.billing_reasons?.length ? (
            <div className="text-xs text-white/70">
              {callSettings.billing_reasons.map((reason) => callBillingReasonUi(reason)).join(' ')}
            </div>
          ) : null}

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Call Settings</div>
            <div className="mt-1 text-xs text-white/70">
              In-app calls use visual consent. External PSTN calls may include a short disclosure when routing is enabled.
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-xs text-white/80">
                Spent today: <span className="font-semibold">{callSettings?.spent_today_pixe || '0.00'} PIXE</span>
              </span>
              {callSettings?.remaining_today_pixe != null ? (
                <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-xs text-white/80">
                  Remaining today: <span className="font-semibold">{callSettings.remaining_today_pixe} PIXE</span>
                </span>
              ) : null}
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-[11px] text-white/60">Effective base price</div>
                <div className="mt-1 text-sm font-semibold">{callSettings?.effective_base_price_pixe || '1.00'} PIXE</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-[11px] text-white/60">Min billable seconds</div>
                <div className="mt-1 text-sm font-semibold">{callSettings?.effective_min_billable_seconds ?? 20}s</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                <div className="text-[11px] text-white/60">Tracking DID</div>
                <div className="mt-1 text-sm font-semibold">{callSettings?.tracking_did_number || 'Not assigned yet'}</div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs text-white/60">Forwarding phone number</label>
                <input
                  value={callSettingsForm.forwarding_phone_number}
                  onChange={(e) => setCallSettingsForm((p) => ({ ...p, forwarding_phone_number: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  placeholder="Defaults to public phone"
                />
              </div>
              <div>
                <label className="text-xs text-white/60">Consent mode</label>
                <select
                  value={callSettingsForm.consent_mode}
                  onChange={(e) => setCallSettingsForm((p) => ({ ...p, consent_mode: e.target.value as 'IN_APP_ONLY' | 'PSTN_FALLBACK' }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none"
                >
                  <option value="IN_APP_ONLY">In-app only</option>
                  <option value="PSTN_FALLBACK">PSTN fallback</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white/60">Base price override (PIXE)</label>
                <input
                  value={callSettingsForm.call_base_price_override_pixe}
                  onChange={(e) => setCallSettingsForm((p) => ({ ...p, call_base_price_override_pixe: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  placeholder="Leave blank to use category base price"
                />
                {!overridePriceValid ? <div className="mt-1 text-xs text-amber-200">Override must be greater than 0.</div> : null}
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input
                    type="checkbox"
                    checked={callSettingsForm.call_routing_enabled}
                    disabled={!hasTrackingDid}
                    onChange={(e) => setCallSettingsForm((p) => ({
                      ...p,
                      call_routing_enabled: e.target.checked,
                      recording_enabled: e.target.checked ? p.recording_enabled : false,
                      transcription_enabled: e.target.checked ? p.transcription_enabled : false,
                    }))}
                  />
                  Call routing (Dedicated DID) {hasTrackingDid ? '' : 'Coming soon'}
                </label>
                {!hasTrackingDid ? <div className="text-xs text-white/60">Requires DID assignment.</div> : null}
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input
                    type="checkbox"
                    checked={callSettingsForm.recording_enabled}
                    disabled={!callRoutingEnabled}
                    onChange={(e) => setCallSettingsForm((p) => ({ ...p, recording_enabled: e.target.checked }))}
                  />
                  Recording (optional) {callRoutingEnabled ? '' : 'Coming soon'}
                </label>
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input
                    type="checkbox"
                    checked={callSettingsForm.transcription_enabled}
                    disabled={!callRoutingEnabled}
                    onChange={(e) => setCallSettingsForm((p) => ({ ...p, transcription_enabled: e.target.checked }))}
                  />
                  Transcription + AI summary {callRoutingEnabled ? '' : 'Coming soon'}
                </label>
              </div>
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={onSaveCallSettings}
                disabled={!callSettingsDirty || callSettingsSaving || !callSettingsFormValid}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${!callSettingsDirty || callSettingsSaving || !callSettingsFormValid ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {callSettingsSaving ? 'Saving...' : 'Save Call Settings'}
              </button>
            </div>
            {callSettingsSuccess ? <div className="mt-2 text-xs text-emerald-300">{callSettingsSuccess}</div> : null}
            {callSettingsError ? <div className="mt-2 text-xs text-red-300">{callSettingsError}</div> : null}
          </div>

          <div className="text-sm font-semibold text-white/90">Performance</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Call clicks</div>
              <div className="mt-2 text-xl font-bold">{callsSummary?.call_clicks_total ?? 0}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Charged clicks</div>
              <div className="mt-2 text-xl font-bold">{callsSummary?.call_clicks_charged ?? 0}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs text-white/60">Cost</div>
              <div className="mt-2 text-xl font-bold">{callsSummary?.spend_pixe_total ?? '0'} PIXE</div>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Pay-Per-Call</div>
            <p className="mt-2 text-sm text-white/75">v1: call button clicks are counted as leads.</p>
            <p className="mt-1 text-sm text-white/75">v2: connected call tracking (duration + quality) is coming later.</p>
            {!effective?.phone ? (
              <div className="mt-2 text-xs text-amber-200">
                Add a phone number to enable Call button tracking. <Link to={`/settings/pages/${effective?.slug}?tab=page_settings`} className="text-brand hover:underline">Open Page Settings</Link>
              </div>
            ) : null}
            <div className="mt-2 text-xs text-white/65">Last 24h clicks: {callsSummary?.last_24h_clicks ?? 0}</div>
            {callsLoading ? <div className="mt-2 text-xs text-white/65">Loading calls data...</div> : null}
            {callsError ? <div className="mt-2 text-xs text-red-300">{callsError}</div> : null}
          </div>

          <div className="text-sm font-semibold text-white/90">Recent Leads</div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Recent leads</div>
              <button
                type="button"
                onClick={onExportCallsCsv}
                disabled={callsExporting}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${callsExporting ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
              >
                {callsExporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>

            {callsEvents.length === 0 && !callsLoading ? <div className="mt-3 text-sm text-white/70">No call clicks yet.</div> : null}

            {callsEvents.length > 0 ? (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="text-white/60">
                    <tr>
                      <th className="px-2 py-2 font-medium">Time</th>
                      <th className="px-2 py-2 font-medium">Source</th>
                      <th className="px-2 py-2 font-medium">Who</th>
                      <th className="px-2 py-2 font-medium">Status</th>
                      <th className="px-2 py-2 font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callsEvents.map((evt) => {
                      const statusUi = callStatusUi(evt.charge_status)
                      return (
                        <tr key={evt.id} className="border-t border-white/10">
                          <td className="px-2 py-2 text-white/80" title={new Date(evt.created_at).toLocaleString()}>
                            {relativeTimeFromNow(evt.created_at)}
                          </td>
                          <td className="px-2 py-2 text-white/80">{evt.source === 'READER' ? 'Reader' : evt.source === 'CARD' ? 'Card' : 'Other'}</td>
                          <td className="px-2 py-2 text-white/80">{evt.actor?.handle || evt.actor?.username || (evt.anon_id ? `Guest ${evt.anon_id}` : 'Guest')}</td>
                          <td className="px-2 py-2">
                            <span className={`rounded-full border px-2 py-0.5 ${statusUi.className}`}>{statusUi.label}</span>
                          </td>
                          <td className="px-2 py-2 text-white/80">{evt.charged_pixe} PIXE</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {callsEventsHasNext ? (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={onLoadMoreCalls}
                  disabled={callsEventsLoadingMore}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold ${callsEventsLoadingMore ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
                >
                  {callsEventsLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'team' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Invite member</div>
            <div className="mt-1 text-xs text-white/70">Add teammates by handle or username.</div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <input
                value={inviteHandle}
                onChange={(e) => setInviteHandle(e.target.value)}
                disabled={!canManageTeam || inviteSaving}
                placeholder="handle or username"
                className={`rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none ${!canManageTeam ? 'text-white/50' : 'focus:ring-2 focus:ring-brand'}`}
              />
              <select
                value={inviteRole}
                disabled={!canManageTeam || inviteSaving}
                onChange={(e) => setInviteRole(e.target.value as 'OWNER' | 'MANAGER' | 'EDITOR' | 'EMPLOYEE')}
                className={`rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none ${!canManageTeam ? 'text-white/50' : ''}`}
              >
                {canAssignOwner ? <option value="OWNER">Owner</option> : null}
                <option value="MANAGER">Manager</option>
                <option value="EDITOR">Editor</option>
                <option value="EMPLOYEE">Employee</option>
              </select>
              <button
                type="button"
                onClick={onAddTeamMember}
                disabled={!canManageTeam || inviteSaving || !inviteHandle.trim()}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${!canManageTeam || inviteSaving || !inviteHandle.trim() ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {inviteSaving ? 'Adding...' : 'Add member'}
              </button>
            </div>
            {!canManageTeam ? <div className="mt-2 text-xs text-white/60">Owner/Manager required to manage team members.</div> : null}
            {inviteError ? <div className="mt-2 text-xs text-red-300">{inviteError}</div> : null}
            {teamError ? <div className="mt-2 text-xs text-red-300">{teamError}</div> : null}
            {teamActionError ? <div className="mt-2 text-xs text-red-300">{teamActionError}</div> : null}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Team</div>
            {teamLoading ? <div className="mt-3 text-sm text-white/70">Loading team...</div> : null}
            {!teamLoading && teamMembers.length === 0 ? (
              <div className="mt-3 text-sm text-white/70">No team members yet.</div>
            ) : null}
            {!teamLoading && teamMembers.length > 0 ? (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs text-white/60">
                    <tr>
                      <th className="px-2 py-2 font-medium">Member</th>
                      <th className="px-2 py-2 font-medium">Role</th>
                      <th className="px-2 py-2 font-medium">Visibility</th>
                      <th className="px-2 py-2 font-medium">Joined</th>
                      <th className="px-2 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map((member) => {
                      const handle = member.user.handle || member.user.username
                      const isBusy = teamBusyMemberId === member.id
                      const rowCanChange = canChangeRole(member) && !isBusy
                      const rowCanRemove = canRemoveMember(member) && !isBusy
                      const visUi = teamPublicStatusUi(member.public_status)
                      return (
                        <tr key={member.id} className="border-t border-white/10">
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-2">
                              {member.user.avatar_url ? (
                                <img src={member.user.avatar_url} alt={handle} className="h-8 w-8 rounded-full object-cover" />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs text-white/70">
                                  {(member.user.display_name || handle).slice(0, 1).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-white">{member.user.display_name || handle}</div>
                                <div className="text-xs text-white/60">@{handle}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <select
                              value={member.role}
                              disabled={!rowCanChange}
                              onChange={(e) => onChangeTeamRole(member, e.target.value as BusinessTeamMember['role'])}
                              className={`rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs outline-none ${!rowCanChange ? 'text-white/50' : ''}`}
                            >
                              {canAssignOwner || member.role === 'OWNER' ? <option value="OWNER">Owner</option> : null}
                              <option value="MANAGER">Manager</option>
                              <option value="EDITOR">Editor</option>
                              <option value="EMPLOYEE">Employee</option>
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${visUi.className}`}>
                              {visUi.label}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-white/80">{new Date(member.created_at).toLocaleDateString()}</td>
                          <td className="px-2 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => onToggleBusinessVisibility(member)}
                                disabled={!canManageTeam || isBusy}
                                className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${!canManageTeam || isBusy ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white/85 hover:bg-white/10'}`}
                              >
                                {member.is_hidden_by_business ? 'Show on public' : 'Hide from public'}
                              </button>
                              <button
                                type="button"
                                onClick={() => onRemoveTeamMember(member)}
                                disabled={!rowCanRemove}
                                className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${!rowCanRemove ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'hours' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">Hours</div>
                <div className="mt-1 text-sm text-white/70">Set weekly business hours and timezone.</div>
              </div>
              <div className="flex items-center gap-2">
                {hoursDirty ? <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-200">Unsaved</span> : null}
                {hoursSuccess ? <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-200">{hoursSuccess}</span> : null}
                <button
                  type="button"
                  onClick={onSaveHours}
                  disabled={!hoursDirty || !hoursValid || hoursSaving || hoursLoading}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${!hoursDirty || !hoursValid || hoursSaving || hoursLoading ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                >
                  {hoursSaving ? 'Saving...' : 'Save Hours'}
                </button>
              </div>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-[240px_1fr]">
              <div>
                <div className="text-xs text-white/60">Timezone</div>
                <input
                  list="timezone-options"
                  value={hoursTimezone}
                  onChange={(e) => {
                    setHoursTimezone(e.target.value)
                    setHoursError(null)
                    setHoursSuccess(null)
                  }}
                  placeholder="America/Los_Angeles"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <datalist id="timezone-options">
                  {resolvedTimezones.map((tz) => (
                    <option key={tz} value={tz} />
                  ))}
                </datalist>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={onCopyWeekdayTemplate} className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10">
                    Copy Mon-Fri
                  </button>
                  <button type="button" onClick={onApplyDefaultWeek} className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10">
                    Default 9-5
                  </button>
                  <button type="button" onClick={onClosedAllWeek} className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10">
                    Closed all week
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {hoursLoading ? <div className="text-sm text-white/70">Loading hours...</div> : null}
                {!hoursLoading ? WEEKDAY_LABELS.map((label, weekday) => {
                  const day = hoursDraft[weekday] || { open: false, ranges: [] }
                  return (
                    <div key={label} className="rounded-lg border border-white/10 bg-black/20 p-3">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <input
                            type="checkbox"
                            checked={day.open}
                            onChange={(e) => setHoursDayOpen(weekday, e.target.checked)}
                          />
                          {label}
                        </label>
                        <button
                          type="button"
                          onClick={() => addHoursRange(weekday)}
                          disabled={!day.open}
                          className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${!day.open ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
                        >
                          Add range
                        </button>
                      </div>
                      {day.open ? (
                        <div className="space-y-2">
                          {day.ranges.map((range, idx) => (
                            <div key={`${label}-${idx}`} className="flex flex-wrap items-center gap-2">
                              <input
                                type="time"
                                value={hhmm(range.opens_at)}
                                onChange={(e) => setHoursRangeField(weekday, idx, 'opens_at', e.target.value)}
                                className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand"
                              />
                              <span className="text-white/70">to</span>
                              <input
                                type="time"
                                value={hhmm(range.closes_at)}
                                onChange={(e) => setHoursRangeField(weekday, idx, 'closes_at', e.target.value)}
                                className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand"
                              />
                              <button
                                type="button"
                                onClick={() => removeHoursRange(weekday, idx)}
                                className="rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-200 hover:bg-red-500/20"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          {day.ranges.length === 0 ? <div className="text-xs text-white/60">No ranges added.</div> : null}
                        </div>
                      ) : (
                        <div className="text-xs text-white/60">Closed</div>
                      )}
                    </div>
                  )
                }) : null}
              </div>
            </div>
            {hoursValidationError ? <div className="mt-2 text-xs text-amber-200">{hoursValidationError}</div> : null}
            {hoursError ? <div className="mt-2 text-xs text-red-300">{hoursError}</div> : null}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Special Hours (Holidays & Exceptions)</div>
            <div className="mt-1 text-xs text-white/70">Date-specific overrides take priority over weekly hours.</div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs text-white/60">Date</label>
                <input
                  type="date"
                  value={specialDate}
                  onChange={(e) => setSpecialDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div>
                <label className="text-xs text-white/60">Note (optional)</label>
                <input
                  value={specialNote}
                  onChange={(e) => setSpecialNote(e.target.value)}
                  placeholder="Holiday, special event, etc."
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-white/85">
                  <input
                    type="checkbox"
                    checked={specialIsClosed}
                    onChange={(e) => setSpecialIsClosed(e.target.checked)}
                  />
                  Closed for this day
                </label>
              </div>
              {!specialIsClosed ? (
                <>
                  <div>
                    <label className="text-xs text-white/60">Opens at</label>
                    <input
                      type="time"
                      value={specialOpensAt}
                      onChange={(e) => setSpecialOpensAt(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60">Closes at</label>
                    <input
                      type="time"
                      value={specialClosesAt}
                      onChange={(e) => setSpecialClosesAt(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                  </div>
                </>
              ) : null}
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={onSaveSpecialHour}
                disabled={!specialDate || specialHourSaving}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${!specialDate || specialHourSaving ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {specialHourSaving ? 'Saving...' : 'Save Special Day'}
              </button>
            </div>
            {specialHoursError ? <div className="mt-2 text-xs text-red-300">{specialHoursError}</div> : null}

            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-white/60">Upcoming overrides</div>
              {specialHoursLoading ? <div className="mt-2 text-sm text-white/70">Loading special hours...</div> : null}
              {!specialHoursLoading && specialHours.length === 0 ? <div className="mt-2 text-sm text-white/70">No special-hour overrides yet.</div> : null}
              {!specialHoursLoading && specialHours.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {specialHours.map((row) => (
                    <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-white">{row.date}</div>
                        <div className="text-white/75">
                          {row.is_closed ? 'Closed' : `${hhmm(String(row.opens_at || ''))} - ${hhmm(String(row.closes_at || ''))}`}
                          {row.note ? ` • ${row.note}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onDeleteSpecialHour(row.id)}
                        disabled={specialHourDeletingId === row.id}
                        className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${specialHourDeletingId === row.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                      >
                        {specialHourDeletingId === row.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'booking' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">Booking settings</div>
                <div className="mt-1 text-sm text-white/70">Enable booking and control slot rules for this page.</div>
              </div>
              <div className="flex items-center gap-2">
                {bookingDirty ? <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-200">Unsaved</span> : null}
                {bookingSuccess ? <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-200">{bookingSuccess}</span> : null}
                <button
                  type="button"
                  onClick={onSaveBookingSettings}
                  disabled={bookingSaving || bookingLoading || !bookingDirty}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${bookingSaving || bookingLoading || !bookingDirty ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                >
                  {bookingSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-white/10 bg-black/20 p-3 md:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold">Stripe Connect</div>
                    <div className="text-xs text-white/65">
                      {bookingStripe?.connected ? 'Stripe connected for deposits (optional).' : 'Connect Stripe to optionally collect booking deposits.'}
                    </div>
                    {bookingStripe?.connected && bookingStripeDetailsOpen ? (
                      <div className="mt-2 space-y-1 rounded-md border border-white/10 bg-black/20 px-2 py-1.5 text-[11px] text-white/65">
                        <div>Account ID: {bookingStripe.account_id || '-'}</div>
                        <div>Charges enabled: {bookingStripe.charges_enabled ? 'Yes' : 'No'}</div>
                        <div>Payouts enabled: {bookingStripe.payouts_enabled ? 'Yes' : 'No'}</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    {bookingStripe?.connected ? (
                      <span className="inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-200">
                        Connected
                      </span>
                    ) : null}
                    {bookingStripe?.connected ? (
                      <button
                        type="button"
                        onClick={() => setBookingStripeDetailsOpen((prev) => !prev)}
                        className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"
                      >
                        {bookingStripeDetailsOpen ? 'Hide details' : 'Details'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={onStartBookingStripeConnect}
                      disabled={bookingStripeConnecting}
                      className={`rounded-lg px-3 py-2 text-sm font-semibold ${bookingStripeConnecting ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                    >
                      {bookingStripe?.connected ? 'Manage' : 'Connect Stripe'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3 md:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold">External Calendars (read-only)</div>
                    <div className="text-xs text-white/65">Connect Google Calendar to block busy times and prevent double booking.</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={onConnectGoogleCalendar}
                      disabled={bookingExternalConnecting}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold ${bookingExternalConnecting ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                    >
                      {bookingExternalConnecting ? 'Connecting...' : 'Connect Google Calendar'}
                    </button>
                    <button
                      type="button"
                      onClick={onSyncExternalCalendars}
                      disabled={bookingExternalSyncing || bookingExternalCalendars.length === 0}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold ${bookingExternalSyncing || bookingExternalCalendars.length === 0 ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'}`}
                    >
                      {bookingExternalSyncing ? 'Syncing...' : 'Sync now'}
                    </button>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {bookingExternalCalendars.length === 0 ? (
                    <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-white/65">
                      No external calendars connected.
                    </div>
                  ) : null}
                  {bookingExternalCalendars.map((row) => (
                    <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2">
                      <div>
                        <div className="text-xs font-semibold text-white">
                          {row.kind === 'google' ? 'Google Calendar' : 'Microsoft Calendar'}
                          {row.is_active ? (
                            <span className="ml-2 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">Active</span>
                          ) : (
                            <span className="ml-2 rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white/70">Disconnected</span>
                          )}
                          {row.needs_reauth ? (
                            <span className="ml-2 rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-200">Needs reauth</span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-[11px] text-white/55">
                          {row.provider_email || 'No account email'} {row.updated_at ? `- updated ${new Date(row.updated_at).toLocaleString()}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => void onDisconnectExternalCalendar(row)}
                        disabled={bookingExternalDisconnectingId === row.id || !row.is_active}
                        className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${bookingExternalDisconnectingId === row.id || !row.is_active ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                      >
                        {bookingExternalDisconnectingId === row.id ? 'Disconnecting...' : 'Disconnect'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <label className="text-sm">
                <span className="text-white/75">Timezone</span>
                <TimezoneSelect
                  value={bookingForm.timezone}
                  onChange={(next) => setBookingForm((prev) => ({ ...prev, timezone: next }))}
                  className="mt-1 w-full max-w-md"
                />
                <button
                  type="button"
                  onClick={onUseBrowserTimezone}
                  className="mt-2 inline-flex w-fit rounded-md border border-white/15 bg-black px-2 py-1 text-xs font-semibold text-white/85 hover:bg-white/10"
                >
                  Use my timezone
                </button>
                <span className="mt-1 block text-xs text-white/55">Used for slot windows and management display.</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  checked={bookingForm.requires_deposit}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, requires_deposit: e.target.checked, deposit_type: e.target.checked ? prev.deposit_type : 'NONE' }))}
                />
                Require booking deposit
              </label>
              {bookingForm.requires_deposit ? (
                <>
                  <label className="text-sm">
                    <span className="text-white/75">Deposit type</span>
                    <select
                      value={bookingForm.deposit_type}
                      onChange={(e) => setBookingForm((prev) => ({ ...prev, deposit_type: e.target.value as 'NONE' | 'FIXED' | 'PERCENT' }))}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    >
                      <option value="FIXED">Fixed</option>
                      <option value="PERCENT">Percent</option>
                    </select>
                  </label>
                  {bookingForm.deposit_type === 'FIXED' ? (
                    <label className="text-sm">
                      <span className="text-white/75">Deposit amount (cents)</span>
                      <input
                        type="number"
                        min={1}
                        value={bookingForm.deposit_amount_cents}
                        onChange={(e) => setBookingForm((prev) => ({ ...prev, deposit_amount_cents: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                      />
                    </label>
                  ) : (
                    <label className="text-sm">
                      <span className="text-white/75">Deposit percent (bps)</span>
                      <input
                        type="number"
                        min={1}
                        max={10000}
                        value={bookingForm.deposit_percent_bps}
                        onChange={(e) => setBookingForm((prev) => ({ ...prev, deposit_percent_bps: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                      />
                    </label>
                  )}
                  <label className="text-sm">
                    <span className="text-white/75">Currency</span>
                    <input
                      value={bookingForm.currency}
                      onChange={(e) => setBookingForm((prev) => ({ ...prev, currency: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                  </label>
                </>
              ) : null}
              <label className="text-sm">
                <span className="text-white/75">Slot duration (minutes)</span>
                <input
                  type="number"
                  min={5}
                  max={480}
                  value={bookingForm.slot_duration_minutes}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, slot_duration_minutes: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Buffer (minutes)</span>
                <input
                  type="number"
                  min={0}
                  max={240}
                  value={bookingForm.buffer_minutes}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, buffer_minutes: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Lead time (minutes)</span>
                <input
                  type="number"
                  min={0}
                  max={10080}
                  value={bookingForm.lead_time_minutes}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, lead_time_minutes: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Booking window (days)</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={bookingForm.booking_window_days}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, booking_window_days: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Max bookings/day (optional)</span>
                <input
                  type="number"
                  min={1}
                  value={bookingForm.max_bookings_per_day}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, max_bookings_per_day: e.target.value }))}
                  placeholder="Unlimited"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={bookingForm.allow_customer_cancel}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, allow_customer_cancel: e.target.checked }))}
                />
                Allow customer cancel
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={bookingForm.allow_customer_reschedule}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, allow_customer_reschedule: e.target.checked }))}
                />
                Allow customer reschedule
              </label>
              <label className="text-sm">
                <span className="text-white/75">Cancel cutoff (minutes)</span>
                <input
                  type="number"
                  min={0}
                  max={43200}
                  disabled={!bookingForm.allow_customer_cancel}
                  value={bookingForm.cancel_cutoff_minutes}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, cancel_cutoff_minutes: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border border-white/10 px-3 py-2 text-sm outline-none ${bookingForm.allow_customer_cancel ? 'bg-black/20 focus:ring-2 focus:ring-brand' : 'cursor-not-allowed bg-black/10 text-white/45'}`}
                />
                {!bookingForm.allow_customer_cancel ? <span className="mt-1 block text-xs text-white/45">Enable customer cancellation to edit this cutoff.</span> : null}
              </label>
              <label className="text-sm">
                <span className="text-white/75">Reschedule cutoff (minutes)</span>
                <input
                  type="number"
                  min={0}
                  max={43200}
                  disabled={!bookingForm.allow_customer_reschedule}
                  value={bookingForm.reschedule_cutoff_minutes}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, reschedule_cutoff_minutes: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border border-white/10 px-3 py-2 text-sm outline-none ${bookingForm.allow_customer_reschedule ? 'bg-black/20 focus:ring-2 focus:ring-brand' : 'cursor-not-allowed bg-black/10 text-white/45'}`}
                />
                {!bookingForm.allow_customer_reschedule ? <span className="mt-1 block text-xs text-white/45">Enable customer rescheduling to edit this cutoff.</span> : null}
              </label>
              <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  checked={bookingForm.is_enabled}
                  onChange={(e) => setBookingForm((prev) => ({ ...prev, is_enabled: e.target.checked }))}
                />
                Booking enabled
              </label>
            </div>
            {bookingError ? <div className="mt-3 text-xs text-red-300">{bookingError}</div> : null}
            {bookingLoading ? <div className="mt-3 text-sm text-white/70">Loading booking settings...</div> : null}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Upcoming bookings (30 days)</div>
            <div className="mt-1 text-sm text-white/70">Basic host view for pending appointments.</div>
            <div className="mt-1 text-xs text-white/60">Times shown in: {bookingDisplayTimezone}</div>
            <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-2">
              <div className="overflow-x-auto">
              {bookingLoading ? <BookingSkeletonTable rows={4} /> : null}
              {!bookingLoading && bookingUpcoming.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-black/30 p-4 text-sm text-white/75">
                  <div className="font-semibold text-white">No upcoming bookings yet.</div>
                  <div className="mt-1 text-xs text-white/60">Bookings confirmed by customers will appear here for the next 30 days.</div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a
                      href={`/b/${effective?.slug || ''}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
                    >
                      View public booking page
                    </a>
                    <button
                      type="button"
                      onClick={onCopyPublicUrl}
                      className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"
                    >
                      {publicUrlCopied ? 'Copied' : 'Copy booking link'}
                    </button>
                  </div>
                </div>
              ) : null}
              {!bookingLoading && bookingUpcoming.length > 0 ? (
                <table className="min-w-full text-sm">
                  <thead className="text-left text-white/60">
                    <tr>
                      <th className="px-2 py-2 font-medium min-w-[190px] whitespace-nowrap">When</th>
                      <th className="px-2 py-2 font-medium">Customer</th>
                      <th className="px-2 py-2 font-medium">Service</th>
                      <th className="px-2 py-2 font-medium">Email</th>
                      <th className="px-2 py-2 font-medium">Phone</th>
                      <th className="px-2 py-2 font-medium">Status</th>
                      <th className="px-2 py-2 font-medium">Payment</th>
                      <th className="px-2 py-2 font-medium">Paid at</th>
                      <th className="px-2 py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingUpcoming.map((row) => {
                      const when = formatWhenCell(row.starts_at, row.ends_at, bookingDisplayTimezone)
                      return (
                      <tr key={row.id} className="border-t border-white/10">
                        <td className="px-2 py-2 text-white/85 min-w-[190px] align-top">
                          <div className="flex min-w-[180px] flex-col" title={when.title}>
                            <div className="whitespace-nowrap font-medium">{when.dateLabel}</div>
                            <div className="whitespace-nowrap text-xs text-white/65">{when.timeLabel}</div>
                          </div>
                        </td>
                        <td className="px-2 py-2">{row.customer_name || '-'}</td>
                        <td className="px-2 py-2">{row.service_name || '-'}</td>
                        <td className="px-2 py-2">{row.customer_email || '-'}</td>
                        <td className="px-2 py-2">{row.customer_phone || '-'}</td>
                        <td className="px-2 py-2">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${row.status === 'CONFIRMED' ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200' : 'border-white/20 bg-white/10 text-white/75'}`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-xs ${bookingPaymentStatusUi(row.payment_status).className}`}>
                              {bookingPaymentStatusUi(row.payment_status).label}
                            </span>
                            <span className="text-xs text-white/65">Deposit {formatUsdCents(row.deposit_amount_cents)}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs text-white/70">{row.paid_at ? formatDateInTimezone(row.paid_at, bookingDisplayTimezone) : '-'}</td>
                        <td className="px-2 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => onResendBookingEmail(row)}
                              disabled={bookingResendingId === row.public_id}
                              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${bookingResendingId === row.public_id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'}`}
                            >
                              {bookingResendingId === row.public_id ? 'Resending...' : 'Resend email'}
                            </button>
                            <button
                              type="button"
                              onClick={() => onOpenRescheduleBooking(row)}
                              disabled={bookingReschedulingId === row.public_id || bookingCancelingId === row.public_id || bookingResendingId === row.public_id}
                              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${bookingReschedulingId === row.public_id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'}`}
                            >
                              {bookingReschedulingId === row.public_id ? 'Rescheduling...' : 'Reschedule'}
                            </button>
                            <button
                              type="button"
                              onClick={() => onDownloadBookingCalendar(row)}
                              className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/10"
                            >
                              {row.status === 'CANCELLED' || row.status === 'REFUNDED' ? 'Download cancellation' : 'Add to calendar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => onCancelBooking(row)}
                              disabled={bookingCancelingId === row.public_id}
                              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${bookingCancelingId === row.public_id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                            >
                              {bookingCancelingId === row.public_id ? 'Cancelling...' : 'Cancel'}
                            </button>
                            {row.payment_status === 'PAID' ? (
                              <button
                                type="button"
                                onClick={() => onRefundBooking(row)}
                                disabled={bookingRefundingId === row.public_id}
                                className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${bookingRefundingId === row.public_id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-sky-400/30 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20'}`}
                              >
                                {bookingRefundingId === row.public_id ? 'Refunding...' : 'Refund'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              ) : null}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Services</div>
              <div className="text-xs text-white/65">{bookingServices.filter((s) => s.is_active).length} active</div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
              <label className="text-sm md:col-span-2">
                <span className="text-white/75">Name</span>
                <input
                  ref={bookingServiceNameInputRef}
                  value={bookingServiceForm.name}
                  onChange={(e) => setBookingServiceForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Duration (min)</span>
                <input
                  type="number"
                  min={1}
                  value={bookingServiceForm.duration_minutes}
                  onChange={(e) => setBookingServiceForm((prev) => ({ ...prev, duration_minutes: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Price (cents, optional)</span>
                <input
                  type="number"
                  min={0}
                  value={bookingServiceForm.price_cents}
                  onChange={(e) => setBookingServiceForm((prev) => ({ ...prev, price_cents: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="text-white/75">Capacity</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={bookingServiceForm.capacity}
                  onChange={(e) => setBookingServiceForm((prev) => ({ ...prev, capacity: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </label>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={onSaveBookingService}
                disabled={bookingServiceSaving}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${bookingServiceSaving ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {bookingServiceSaving ? 'Saving...' : bookingServiceForm.id ? 'Save service' : 'Add service'}
              </button>
              {bookingServiceForm.id ? (
                <button
                  type="button"
                  onClick={resetBookingServiceForm}
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold hover:bg-white/10"
                >
                  Cancel edit
                </button>
              ) : null}
            </div>
            {bookingServiceError ? <div className="mt-2 text-xs text-red-300">{bookingServiceError}</div> : null}

            <div className="mt-3 space-y-2">
              {bookingLoading ? <BookingSkeletonRows count={3} /> : null}
              {!bookingLoading && bookingServices.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-black/30 p-4 text-sm text-white/75">
                  <div className="font-semibold text-white">Add at least one service so customers can book.</div>
                  <div className="mt-1 text-xs text-white/60">Services define duration, pricing, and capacity for booking slots.</div>
                  <button
                    type="button"
                    onClick={() => bookingServiceNameInputRef.current?.focus()}
                    className="mt-3 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
                  >
                    Add service
                  </button>
                </div>
              ) : null}
              {!bookingLoading && bookingServices.map((service) => (
                <div key={service.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm">
                  <div>
                    <div className="font-semibold text-white">{service.name}</div>
                    <div className="text-xs text-white/65">
                      {service.duration_minutes} min • Cap {service.capacity || 1} • {service.price_cents == null ? 'No price' : `$${(service.price_cents / 100).toFixed(2)}`} • {service.is_active ? 'Active' : 'Disabled'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onEditBookingService(service)}
                      className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10"
                    >
                      Edit
                    </button>
                    {service.is_active ? (
                      <button
                        type="button"
                        onClick={() => void onDisableBookingService(service)}
                        className="rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-1.5 text-xs font-semibold text-red-200 hover:bg-red-500/20"
                      >
                        Disable
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Staff</div>
              <div className="text-xs text-white/65">{bookingStaff.filter((s) => s.is_active).length} active</div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
              <input
                ref={bookingStaffLookupInputRef}
                value={bookingStaffLookup}
                onChange={(e) => setBookingStaffLookup(e.target.value)}
                placeholder="Add staff by handle or email"
                className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
              />
              <button
                type="button"
                onClick={() => void onAddBookingStaff()}
                disabled={!bookingStaffLookup.trim() || bookingStaffAdding}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${!bookingStaffLookup.trim() || bookingStaffAdding ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {bookingStaffAdding ? 'Adding...' : 'Add staff'}
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {bookingLoading ? <BookingSkeletonRows count={3} /> : null}
              {!bookingLoading && bookingStaff.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-black/30 p-4 text-sm text-white/75">
                  <div className="font-semibold text-white">Add staff to accept bookings.</div>
                  <div className="mt-1 text-xs text-white/60">Staff can be marked public and assigned custom schedules.</div>
                  <button
                    type="button"
                    onClick={() => bookingStaffLookupInputRef.current?.focus()}
                    className="mt-3 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
                  >
                    Add staff
                  </button>
                </div>
              ) : null}
              {!bookingLoading && bookingStaff.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => setBookingStaffSelectedId(member.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left ${member.id === bookingStaffSelectedId ? 'border-brand bg-brand/10' : 'border-white/10 bg-black/30 hover:bg-white/5'}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">{member.user.display_name}</div>
                    <div className="text-xs text-white/60">@{member.user.handle}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <label className="inline-flex items-center gap-2 text-white/80">
                      <input
                        type="checkbox"
                        checked={member.is_active}
                        onChange={(e) => void onToggleBookingStaff(member, 'is_active', e.target.checked)}
                      />
                      Active
                    </label>
                    <label className="inline-flex items-center gap-2 text-white/80">
                      <input
                        type="checkbox"
                        checked={member.is_public}
                        onChange={(e) => void onToggleBookingStaff(member, 'is_public', e.target.checked)}
                      />
                      Public
                    </label>
                    <button
                      type="button"
                      onClick={() => void onRemoveBookingStaff(member)}
                      className="rounded border border-red-400/30 bg-red-500/10 px-2 py-1 font-semibold text-red-200 hover:bg-red-500/20"
                    >
                      Remove
                    </button>
                  </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <StaffScheduleEditor
            title="Staff schedule"
            staffLabel={selectedBookingStaff ? `${selectedBookingStaff.user.display_name} (@${selectedBookingStaff.user.handle})` : 'No staff selected'}
            staffId={selectedBookingStaff?.id ?? null}
            loadHours={(staffId) => getBusinessStaffHours(effective.slug, staffId)}
            saveHours={(staffId, rows) => replaceBusinessStaffHours(effective.slug, staffId, rows)}
            loadSpecialHours={(staffId) => getBusinessStaffSpecialHours(effective.slug, staffId)}
            saveSpecialHour={(staffId, payload) => upsertBusinessStaffSpecialHour(effective.slug, staffId, payload)}
            deleteSpecialHour={(staffId, specialId) => deleteBusinessStaffSpecialHour(effective.slug, staffId, specialId)}
            loadTimeOff={(staffId) => getBusinessStaffTimeOff(effective.slug, staffId)}
            createTimeOff={(staffId, payload) => createBusinessStaffTimeOff(effective.slug, staffId, payload)}
            deleteTimeOff={(staffId, timeoffId) => deleteBusinessStaffTimeOff(effective.slug, staffId, timeoffId)}
            onToast={onStaffScheduleToast}
          />
        </div>
      ) : null}

      {tab === 'page_settings' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Profile completeness: {completenessScore}%</div>
                <div className="mt-2 h-1.5 w-56 rounded-full bg-white/10">
                  <div className="h-1.5 rounded-full bg-brand" style={{ width: `${completenessScore}%` }} />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {settingsDirty ? <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-200">Unsaved changes</span> : null}
                {saveSuccess ? <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-200">{saveSuccess}</span> : null}
                <button
                  type="button"
                  onClick={onResetPageSettings}
                  disabled={!settingsDirty || saveLoading}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${!settingsDirty || saveLoading ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={onSavePageSettings}
                  disabled={!settingsDirty || !settingsValid || saveLoading}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${!settingsDirty || !settingsValid || saveLoading ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                >
                  {saveLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
            {saveError ? <div className="mt-2 text-xs text-red-300">{saveError}</div> : null}
          </div>

          <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-sky-100">Need image uploads and story editor?</div>
                <div className="mt-1 text-xs text-sky-100/80">
                  Use the full editor for portrait/header uploads, avatar upload, and rich story editing.
                </div>
              </div>
              <Link to={`/settings/pages/${effective?.slug}/edit`} className="inline-flex rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600">
                Open Full Editor
              </Link>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Basic Info</div>
                <div className="mt-1 text-sm text-white/70">Update your business listing details.</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-white/55">Public URL</div>
                <div className="mt-1 text-xs text-white/80">{publicUrl || `/b/${effective?.slug || ''}`}</div>
                <button type="button" onClick={onCopyPublicUrl} className="mt-1 text-xs text-brand hover:underline">
                  {publicUrlCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <input value={preview.name} onChange={(e) => setPreviewField('name', e.target.value)} placeholder="Name" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.name ? <div className="mt-1 text-xs text-red-300">{fieldErrors.name}</div> : null}
              </div>
              <div>
                <input value={preview.category} onChange={(e) => setPreviewField('category', e.target.value)} placeholder="Category" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.category ? <div className="mt-1 text-xs text-red-300">{fieldErrors.category}</div> : null}
              </div>
              <div className="md:col-span-2">
                <textarea value={preview.description} onChange={(e) => setPreviewField('description', e.target.value)} placeholder="Short description" rows={3} className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                <div className="mt-1 text-xs text-white/65">
                  Shown in listings and search results. For your full story, use{' '}
                  <Link to={`/settings/pages/${effective?.slug}/edit`} className="text-brand hover:underline">Edit Page</Link>.
                </div>
                {fieldErrors.description ? <div className="mt-1 text-xs text-red-300">{fieldErrors.description}</div> : null}
              </div>
              <div>
                <input value={preview.phone} onChange={(e) => setPreviewField('phone', e.target.value)} placeholder="Phone" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.phone ? <div className="mt-1 text-xs text-red-300">{fieldErrors.phone}</div> : null}
              </div>
              <div>
                <input value={preview.website} onChange={(e) => setPreviewField('website', e.target.value)} placeholder="Website" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.website ? <div className="mt-1 text-xs text-red-300">{fieldErrors.website}</div> : null}
                {!previewWebsiteValid ? <div className="mt-1 text-xs text-amber-200">Website should be a valid http/https URL.</div> : null}
              </div>
              <div className="md:col-span-2">
                <input value={preview.address} onChange={(e) => setPreviewField('address', e.target.value)} placeholder="Address" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.address ? <div className="mt-1 text-xs text-red-300">{fieldErrors.address}</div> : null}
              </div>
            </div>
            {!settingsNameValid ? <div className="mt-2 text-xs text-amber-200">Name is required.</div> : null}
            {!settingsCategoryValid ? <div className="mt-2 text-xs text-amber-200">Category is required.</div> : null}
            <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="text-sm font-semibold">About / Story</div>
              <div className="mt-1 text-sm text-white/70">
                Your full story appears on your public page. Use Edit Page to update your story, images, and preview.
              </div>
              <Link to={`/settings/pages/${effective?.slug}/edit`} className="mt-2 inline-flex text-sm font-semibold text-brand hover:underline">
                Edit Page
              </Link>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Location</div>
            <div className="mt-1 text-sm text-white/70">Location is used for local discovery and call ads targeting.</div>
            <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3 text-sm text-white/80">
              {locationSet ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    Location set: {formatCoord(parsedLat)}, {formatCoord(parsedLng)}
                  </div>
                  <button type="button" onClick={onClearLocation} className="text-xs font-semibold text-red-200 hover:underline">
                    Clear
                  </button>
                </div>
              ) : (
                <div>No location set yet.</div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onUseCurrentLocation}
                disabled={locating}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${locating ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
              >
                {locating ? 'Locating...' : 'Use my current location'}
              </button>
              <button
                type="button"
                onClick={() => setShowMapPicker(true)}
                className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold hover:bg-white/10"
              >
                Pick on map
              </button>
            </div>
            <div className="mt-3">
              <div className="text-xs text-white/60">Paste maps link or coordinates</div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <input
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  placeholder="32.123, -116.567 or https://maps.google.com/..."
                  className="min-w-[280px] flex-1 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <button
                  type="button"
                  onClick={onApplyPastedLocation}
                  disabled={!locationInput.trim()}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold ${!locationInput.trim() ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 hover:bg-white/10'}`}
                >
                  Apply
                </button>
              </div>
            </div>
            {locationNotice ? <div className="mt-2 text-xs text-white/70">{locationNotice}</div> : null}
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowAdvancedLocation((v) => !v)}
                className="text-xs font-semibold text-white/75 hover:text-white hover:underline"
              >
                {showAdvancedLocation ? 'Hide Advanced' : 'Advanced'}
              </button>
            </div>
            {showAdvancedLocation ? (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div>
                  <input value={preview.lat} onChange={(e) => setPreviewField('lat', e.target.value)} placeholder="Latitude" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                  {fieldErrors.lat ? <div className="mt-1 text-xs text-red-300">{fieldErrors.lat}</div> : null}
                </div>
                <div>
                  <input value={preview.lng} onChange={(e) => setPreviewField('lng', e.target.value)} placeholder="Longitude" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                  {fieldErrors.lng ? <div className="mt-1 text-xs text-red-300">{fieldErrors.lng}</div> : null}
                </div>
              </div>
            ) : null}
            {!latLngPairValid ? <div className="mt-2 text-xs text-amber-200">Latitude and longitude must be set together.</div> : null}
            {!latValid || !lngValid ? <div className="mt-2 text-xs text-amber-200">Latitude/longitude must be valid numbers.</div> : null}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-sm font-semibold">Branding</div>
            <div className="mt-1 text-sm text-white/70">Use high-resolution images. Logo should be square.</div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <input value={preview.logo_image_url} onChange={(e) => setPreviewField('logo_image_url', e.target.value)} placeholder="Logo image URL" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.logo_image_url ? <div className="mt-1 text-xs text-red-300">{fieldErrors.logo_image_url}</div> : null}
              </div>
              <div>
                <input value={preview.cover_image_url} onChange={(e) => setPreviewField('cover_image_url', e.target.value)} placeholder="Cover image URL" className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                {fieldErrors.cover_image_url ? <div className="mt-1 text-xs text-red-300">{fieldErrors.cover_image_url}</div> : null}
              </div>
            </div>
            <div className="mt-4 text-sm font-semibold">Public header preview</div>
            <div className="mt-3 overflow-hidden rounded-2xl border border-white/10 bg-black/20">
              <div className="relative h-40 md:h-52 bg-black/30">
                {preview.cover_image_url ? <img src={preview.cover_image_url} alt="cover preview" className="h-full w-full object-cover" /> : null}
              </div>
              <div className="px-4 pb-4">
                <div className="-mt-9 flex items-end gap-3">
                  <div className="h-20 w-20 rounded-2xl border border-white/15 bg-black/40 overflow-hidden">
                    {preview.logo_image_url ? <img src={preview.logo_image_url} alt="logo preview" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="pb-1">
                    <div className="text-xl font-extrabold">{preview.name || 'Business name'}</div>
                    <div className="mt-1 inline-flex rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-xs">
                      {preview.category || 'Category'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <LocationPickerModal
        open={showMapPicker}
        initialLat={parsedLat}
        initialLng={parsedLng}
        onClose={() => setShowMapPicker(false)}
        onConfirm={(lat, lng) => {
          setLocationValues(lat, lng)
          setLocationNotice(`Location selected: ${formatCoord(lat)}, ${formatCoord(lng)}.`)
          setShowMapPicker(false)
        }}
      />
      <div className="pt-2 text-right text-[11px] text-white/45">
        {appVersionMeta?.git_sha ? `Version ${appVersionMeta.git_sha.slice(0, 12)}${appVersionMeta.env ? ` • ${appVersionMeta.env}` : ''}` : ''}
      </div>
    </div>
  )
}
